import {ComponentFactoryResolver, Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {OverlayManagerService} from '../interfaces/viewer/overlay-manager-service';
import {DicomHelperService} from '../interfaces/viewer/dicom-helper-service';
import {DicomTag} from '../interfaces/viewer/dicom-tag';
import {SeriesManagerService} from '../interfaces/viewer/series-manager.service';
import {ImagingProjectService} from '../imaging-project.service';
import {ObjectRetrieveService} from '../interfaces/viewer/object-retrieve-service';
import { QueryArchiveService } from '../interfaces/viewer/query-archive.service';
import {environment} from 'src/environments/environment';
import {ViewerAuthenticationService} from '../interfaces/viewer/viewer-authentication-service';
import * as d3 from 'd3';
import {OverlayTag} from '../../_models/viewer/OverlayTagModel';
import { DOWN_ARROW, UP_ARROW } from '@angular/cdk/keycodes';
import { MapInfo, QueryGradient, SeriesData } from 'src/app/_models/viewer/QueryOptions';
import throttle from 'lodash/throttle';
import {OverlayTags, Utils} from './lead-tools-utils';
import { ViewerAuditableContext } from 'src/app/_models/viewer/ViewerConfigModel';

const ROOT_URL = environment.Leadtools.rootURL;
const SERVICES = environment.Leadtools.services;
@Injectable({
    providedIn: 'root'
})
export class LeadToolsOverlayManagerService implements OverlayManagerService {

    private removeSeriesCellSubj = new Subject<any>();
    private sliceChanged = new Subject<any>();
    private _defaultOverlayTags: any = {};
    private _is_custom_overlays: any = {};
    private _parametricMaps: any = {};
    private readonly _retrieveLocalUrl: any = {};
    private _normalisedIntensityGraph: any = {};
    public name2OverlayIds: any = {};
    public overlayId2names: any = {};
    public cellOverlays: any = {};
    public removeSeriesCell = this.removeSeriesCellSubj.asObservable();
    public cellsliceChanged = this.sliceChanged.asObservable();
    public sliceSyncStatus: boolean = false;
    private wheelCell = null;
    public toolbarItems: any = {};
    public patientStudyID = 0;

    constructor(
        private authenticationService: ViewerAuthenticationService,
        private imagingProjectService: ImagingProjectService,
        private dicomHelperService: DicomHelperService,
        private objectRetrieveService: ObjectRetrieveService,
        private seriesManagerService: SeriesManagerService,
        private queryArchiveService: QueryArchiveService,
        private utils: Utils) {
            this._retrieveLocalUrl = ROOT_URL + '/' + SERVICES.objectRetrieveLocalServiceName;
        }

    clearRemoveButtonSubscription() {
      this.removeSeriesCellSubj.next(null);
    }

    getOverlayType(option: string): any {
        switch (option) {
            case "Window Level":
                return lt.Controls.Medical.OverlayTextType.windowLevel;
            case "Instance Number":
                return lt.Controls.Medical.OverlayTextType.instanceNumber;
            case "User Data":
                return lt.Controls.Medical.OverlayTextType.userData;
            case "Image Quality":
                return lt.Controls.Medical.OverlayTextType.imageQuality;
            case "Frame Number":
                return lt.Controls.Medical.OverlayTextType.frameNumber;
            case "Left Orientation":
                return lt.Controls.Medical.OverlayTextType.leftOrientation;
            case "Right Orientation":
                return lt.Controls.Medical.OverlayTextType.rightOrientation;
            case "Top Orientation":
                return lt.Controls.Medical.OverlayTextType.topOrientation;
            case "Bottom Orientation":
                return lt.Controls.Medical.OverlayTextType.bottomOrientation;
        }
        return lt.Controls.Medical.OverlayTextType.userData;
    }

    getOverlayTypeName(type: any): string {
        switch (type) {
            case lt.Controls.Medical.OverlayTextType.windowLevel:
                return "Window Level";
            case lt.Controls.Medical.OverlayTextType.instanceNumber:
                return "Instance Number";
            case lt.Controls.Medical.OverlayTextType.userData:
                return "User Data";
            case lt.Controls.Medical.OverlayTextType.imageQuality:
                return "Image Quality";
            case lt.Controls.Medical.OverlayTextType.frameNumber:
                return "Frame Number";
            case lt.Controls.Medical.OverlayTextType.leftOrientation:
                return "Left Orientation";
            case lt.Controls.Medical.OverlayTextType.rightOrientation:
                return "Right Orientation";
            case lt.Controls.Medical.OverlayTextType.topOrientation:
                return "Top Orientation";
            case lt.Controls.Medical.OverlayTextType.bottomOrientation:
                return "Bottom Orientation";
        }
        return "UserData";
    }

    getOverlay(cell: lt.Controls.Medical.Cell, name): lt.Controls.Medical.OverlayText {
      if (this.name2OverlayIds[cell.divID] && this.name2OverlayIds[cell.divID][name]) {
        const olIdx = this.name2OverlayIds[cell.divID][name];
        return cell.get_overlays().get_item(olIdx);
      }
      return null;
    }

    setOverlay(cell: lt.Controls.Medical.Cell, name, overlay: lt.Controls.Medical.OverlayText ): void {
      if (this.name2OverlayIds[cell.divID] && this.name2OverlayIds[cell.divID][name]) {
        const olIdx = this.name2OverlayIds[cell.divID][name];
        cell.get_overlays().set_item(olIdx, overlay);
      }
    }

    addTag(cell: any, text: any, alignment: any, overlayType: any, positionIndex: any, color?: any, name?) {
      const overlay = new lt.Controls.Medical.OverlayText();

      overlay.set_text(text);
      overlay.set_alignment(alignment);
      overlay.set_type(overlayType);
      overlay.set_positionIndex(positionIndex);
      overlay.set_color(color || '#FFFFFF');

      if (overlayType === lt.Controls.Medical.OverlayTextType.windowLevel) {
        overlay.set_weight(10);
      }

      if (!(cell instanceof lt.Controls.Medical.Cell3D)){
        const length = cell.get_overlays().count;
        cell.get_overlays().add(overlay);

        if (name != null && name !== '') {
          if (this.name2OverlayIds[cell.divID]) {
            this.name2OverlayIds[cell.divID][name] = length;
            this.overlayId2names[cell.divID][length] = name;
          } else {
            this.name2OverlayIds[cell.divID] = {[name]: length};
            this.overlayId2names[cell.divID] = {[length]: name};
          }
        }
      } else {
        const cell3D: lt.Controls.Medical.Cell3D = <lt.Controls.Medical.Cell3D>cell;
        cell3D.labels.add(overlay);
      }
    }

    cellHasOverlays(cell: lt.Controls.Medical.Cell): boolean {
      return cell.overlays.count > 0;
    }

    build_gradient_canvas(cell, image, map_name, minVal, maxVal) {
      // console.log('build_gradient_canvas...');
      const new_canvasElt = document.createElement('canvas') as HTMLCanvasElement;
      new_canvasElt.setAttribute('id', cell.divID+'_gd-overlay');
      new_canvasElt.setAttribute('style', 'position: absolute; top: 2px; left: 2px; display: none');
      const ctx = new_canvasElt.getContext("2d");
      const cell_H = parseInt(cell.get_div().style.height);
      const cell_W = parseInt(cell.get_div().style.width);
      ctx.canvas.height = cell_H;
      ctx.canvas.width = cell_W;

      let ratio_w = 0.10;
      let ratio_h = 0.4;
      const gd_ratio = 109/179;

      let x_offset = 0;
      let y_offset = 0;
      let min_size = 0;
      if(ctx.canvas.width > ctx.canvas.height) {
        x_offset = ctx.canvas.width - (ctx.canvas.height*ratio_w)*2;
        y_offset = (ctx.canvas.height - ctx.canvas.height*ratio_h)/2;
        min_size = ctx.canvas.height;
      } else {
        x_offset = ctx.canvas.width - (ctx.canvas.width*ratio_w*2) + 10;
        y_offset = (ctx.canvas.height - ctx.canvas.height*ratio_h)/2 + (ctx.canvas.height-ctx.canvas.width)/4;
        min_size = ctx.canvas.width;
      }

      let gd_w = min_size*ratio_w;
      let gd_h = min_size*ratio_h;
      if (map_name === 'Gadolinium') {
        ratio_w = 0.2;
        const _gd_w = 179;
        const _gd_h = 109;
        const _gd_ratio = _gd_h/_gd_w;
        gd_w = min_size*ratio_w;
        gd_h = gd_w*_gd_ratio
        x_offset = 10;
        y_offset = ctx.canvas.height*(3/4) - gd_h*2;
      }
      // console.log('gd_w:', gd_w);
      // console.log('gd_h:', gd_h);
      // console.log('image.style:', image.style);
      ctx.drawImage(image, x_offset, y_offset, gd_w, gd_h);

      // no font-style for Gadolinium's gradient
      if (map_name !== 'Gadolinium') {
        // set inline border
        ctx.strokeStyle = 'white'; //'#e38700' ffffff
        ctx.lineWidth = 2;
        ctx.strokeRect(x_offset, y_offset, gd_w-2, gd_h-2);

        // set range values
        const font_size = Math.floor(gd_w*0.22);
        ctx.font = font_size+"px serif";
        // console.log('ctx.font:', ctx.font);

        const maps_gray_texts = ['Time To Peak', 'Time of Onset', 'Time to Washout', 'Gadolinium'];
        if (maps_gray_texts.some(item => map_name === item)) {
          ctx.fillStyle = 'gray';
          ctx.fillText(maxVal, x_offset+gd_w*0.2, y_offset+gd_h*0.088);
          ctx.fillStyle = 'gray';
          ctx.fillText(minVal, x_offset+gd_w*0.4, y_offset+(min_size*ratio_h)-gd_h*0.07);
        }
        else {
          ctx.fillStyle = 'black';
          ctx.fillText(maxVal, x_offset+gd_w*0.2, y_offset+gd_h*0.088);
          ctx.fillStyle = 'white';
          ctx.fillText(minVal, x_offset+gd_w*0.4, y_offset+(min_size*ratio_h)-gd_h*0.07);
        }

        const imageData = ctx.getImageData(x_offset, y_offset, min_size, min_size);

        // Get colormap
        const colorMap = this.get_colormap_from_mapname(map_name);
        if (map_name === 'rCBV Reg' || map_name === 'FTB Reg' || map_name === 'K2 Reg' || map_name === 'Tofts Ktrans Reg'
         || map_name === 'Tofts ve Reg' || map_name === 'Tofts vp Reg' || map_name === 'ADC Reg') {
          this.applyColormap(imageData.data, colorMap, true, map_name);
        } else {
          this.applyColormap(imageData.data, colorMap);
        }
        ctx.putImageData(imageData, x_offset, y_offset);
      }

      return new_canvasElt;
    }

    build_mapped_canvas(cell: lt.Controls.Medical.Cell, image: HTMLImageElement, map_name) {
      // console.log('build_mapped_canvas...');
      const im_item: lt.Controls.ImageViewerItem = cell.get_imageViewer().get_activeItem();
      const annoCanvas = (<any>im_item).annotationCanvas as HTMLCanvasElement;
      // console.log('im_item:', im_item);

      if (cell === null || cell === undefined)
        return;
      if (cell.get_frames().toArray().length <= 0)
        return;

      const new_canvasElt = document.createElement('canvas') as HTMLCanvasElement;
      new_canvasElt.setAttribute('id', cell.divID+'_pm-overlay');
      new_canvasElt.setAttribute('style', 'opacity: 0.8; top: 2px; left: 2px; position: absolute; display: none');
      // avoid mismatch due highlight of active cell
      new_canvasElt.style.top = annoCanvas.style.top;
      new_canvasElt.style.left = annoCanvas.style.left;

      const ctx = new_canvasElt.getContext("2d");
      ctx.canvas.height = annoCanvas.height;
      ctx.canvas.width = annoCanvas.width;
      const transform = im_item.get_transform();
      const imageSize = im_item.get_imageSize();
      const imageScale = im_item.get_scale();

      let x_offset = transform.get_offsetX();
      let y_offset = transform.get_offsetY();
      let canvas_W = imageSize.get_width()*imageScale.get_width();
      let canvas_H = imageSize.get_height()*imageScale.get_height();

      // apply rotation action
      const curOffset = cell.get_currentOffset();
      const cellFrame: lt.Controls.Medical.Frame = cell.get_frames().toArray()[curOffset];
      const angle = cellFrame.get_rotateAngle();
      const flipped = cellFrame.flipped;
      const reversed = cellFrame.reversed;

      if (angle !== 0) {
        // save the unrotated context
        ctx.save();
        // move to the center of the canvas (top-left)
        ctx.translate(transform.get_offsetX(), transform.get_offsetY());
        // rotate the canvas to the specified degrees
        ctx.rotate(angle*Math.PI/180);
        // cancel offsets
        x_offset = 0;
        y_offset = 0;
      }

      let scaleX = reversed ? -1 : 1; // reverse
      let scaleY = flipped ? -1: 1; // flip

      // settings for rotation, flip and reverse actions
      if (angle === 90) {
        canvas_W = imageSize.get_width()*imageScale.get_width();
        canvas_H = -imageSize.get_height()*imageScale.get_height();
        scaleX = flipped ? -1: 1; // flip
        scaleY = reversed ? -1 : 1; // reverse
      }
      else if (angle === 180) {
        canvas_W = -imageSize.get_width()*imageScale.get_width();
        canvas_H = -imageSize.get_height()*imageScale.get_height();
      }
      else if (angle === 270) {
        canvas_W = -imageSize.get_width()*imageScale.get_width();
        canvas_H = imageSize.get_height()*imageScale.get_height();
        scaleX = flipped ? -1: 1; // flip
        scaleY = reversed ? -1 : 1; // reverse
      }

      // apply scale to axis
      ctx.scale(scaleX, scaleY);

      ctx.drawImage(image, x_offset*scaleX, y_offset*scaleY, canvas_W*scaleX, canvas_H*scaleY);

      if (angle !== 0) {
        // restore the unrotated context
        ctx.restore();
        // reset offsets and canvas W & H
        x_offset = transform.get_offsetX();
        y_offset = transform.get_offsetY();
        canvas_W = imageSize.get_width()*imageScale.get_width();
        canvas_H = imageSize.get_height()*imageScale.get_height();
      }

      const imageData = ctx.getImageData(x_offset, y_offset, canvas_W, canvas_H);

      if (this.patientStudyID != 81274) {
        // Get colormap
        const colorMap = this.get_colormap_from_mapname(map_name);
        if (map_name === 'rCBV Reg' || map_name === 'FTB Reg' || map_name === 'K2 Reg' || map_name === 'Tofts Ktrans Reg'
        || map_name === 'Tofts ve Reg' || map_name === 'Tofts vp Reg' || map_name === 'ADC Reg') {
          this.applyColormap(imageData.data, colorMap, true, map_name);
        } else {
          this.applyColormap(imageData.data, colorMap);
        }
      }

      ctx.putImageData(imageData, x_offset, y_offset);

      // apply opacity
      if((<any>cell).pm_opacity >= 0) {
        new_canvasElt.style.opacity = (<any>cell).pm_opacity.toString();
      }
      return new_canvasElt;
    }

    remove_mapped_canvas(cell) {
      if(!this._parametricMaps || !this._parametricMaps[cell.divID]
        || !this._parametricMaps[cell.divID]['mapCodeSelected']
        || !this._parametricMaps[cell.divID]['mapSeriesUIDSelected'])
      { return;} // only performs if map selected
      const cur_canvasElt_map = document.getElementById(cell.divID + '_pm-overlay')  as HTMLCanvasElement;
      if(cur_canvasElt_map !== null) {
        // remove current canvas
        cur_canvasElt_map.style.display = 'none';
        cell.get_div().removeChild(cur_canvasElt_map);
      }
    }

    redraw_mapped_canvas(cell: lt.Controls.Medical.Cell) {
      if(!this._parametricMaps || !this._parametricMaps[cell.divID]
        || !this._parametricMaps[cell.divID]['mapCodeSelected']
        || !this._parametricMaps[cell.divID]['mapSeriesUIDSelected'])
      { return;} // only performs if map selected

      try {
        const seriesInfo = this.seriesManagerService.getSeriesInfo(cell)
        const map_code = this._parametricMaps[cell.divID]['mapCodeSelected'];
        const map_name = this._parametricMaps[cell.divID]['mapNameSelected'];
        const seriesiuid = this._parametricMaps[cell.divID]['mapSeriesUIDSelected'];
        const numbOfSlices = seriesInfo.numbOfSlices;
        const phantomSliceNumb = this.getRemainder(cell.currentOffset, numbOfSlices);
        const cell_H = parseInt(cell.get_div().style.height);
        const cell_W = parseInt(cell.get_div().style.width);

        // redrawing map on canvas
        const imageMap = this._parametricMaps[cell.divID][map_code][seriesiuid]['imageDOM'][phantomSliceNumb];
        const cur_canvasElt_map = document.getElementById(cell.divID + '_pm-overlay')  as HTMLCanvasElement;
        if(cur_canvasElt_map !== null) {
          // remove current canvas
          cur_canvasElt_map.style.display = 'none';
          cell.get_div().removeChild(cur_canvasElt_map);
        }

        if(cell_H !== 0 && cell_W  !== 0) {
          // build canvas for PM with colormap applied
          const new_canvasElt_map = this.build_mapped_canvas(cell, imageMap, map_name);
          // add new canvas to the cell
          new_canvasElt_map.style.display = 'block';
          new_canvasElt_map.style.opacity = (<any>cell).pm_opacity.toString();
          cell.get_div().appendChild(new_canvasElt_map);
        }
      }
      catch(err) {
        console.log("WARN > caught:", err);
      }
    }

    redraw_gradient_canvas(cell: lt.Controls.Medical.Cell) {
      if(!this._parametricMaps || !this._parametricMaps[cell.divID]
        || !this._parametricMaps[cell.divID]['mapCodeSelected']
        || !this._parametricMaps[cell.divID]['mapSeriesUIDSelected'])
      { return;} // only performs if map selected

      try {
        const seriesInfo = this.seriesManagerService.getSeriesInfo(cell)
        const map_code = this._parametricMaps[cell.divID]['mapCodeSelected'];
        const map_name = this._parametricMaps[cell.divID]['mapNameSelected'];
        const seriesiuid = this._parametricMaps[cell.divID]['mapSeriesUIDSelected'];
        const numbOfSlices = seriesInfo.numbOfSlices;
        const phantomSliceNumb = this.getRemainder(cell.currentOffset, numbOfSlices);
        const cell_H = parseInt(cell.get_div().style.height);
        const cell_W = parseInt(cell.get_div().style.width);

        // redraw gradient on canvas
        const imageGradient = this._parametricMaps[cell.divID][map_code][seriesiuid]['gradientDOM'];
        const cur_canvasElt_gd = document.getElementById(cell.divID + '_gd-overlay')  as HTMLCanvasElement;
        const gd = (<any>cell).gradientData && (<any>cell).gradientData[map_name] ? (<any>cell).gradientData[map_name] : null;

        if(cur_canvasElt_gd !== null && gd !== null && cell_H !== 0 && cell_W  !== 0) {
          // remove current canvas
          cur_canvasElt_gd.style.display = 'none';
          cell.get_div().removeChild(cur_canvasElt_gd);

          const minVal = gd.rangeData.min;
          const maxVal = gd.rangeData.max;

          // build canvas for gradient with colormap applied
          const new_canvasElt_gd = this.build_gradient_canvas(cell, imageGradient, map_name, minVal, maxVal);
          // add new canvas to the cell
          new_canvasElt_gd.style.display = 'block';
          cell.get_div().appendChild(new_canvasElt_gd);
        }
      }
      catch(err) {
        console.log("WARN > caught:", err);
      }
    }

    setCellMCOverlayText(cell: lt.Controls.Medical.Cell, text: string): void {
      const overlay = this.getOverlay(cell, 'mc-content');
      overlay.set_text(text);
    }

    addCustomOverlays(cell: lt.Controls.Medical.Cell): void {
      // console.log('addCustomOverlays...');
      this.cellOverlays[cell.divID].isReady = true;
      const cell_div = cell.get_div();
      const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
      seriesInfo.curFrameNumb = 1;

        if (seriesInfo.mapName === '2D Motion Correction') {
          this.setCellMCOverlayText(cell, seriesInfo.mapName);
        }

      const slice_count = (seriesInfo && seriesInfo.numbOfSlices) ? seriesInfo.numbOfSlices
        : this.seriesManagerService.getInstances(cell.seriesInstanceUID, cell.divID).length;
      this.cellOverlays[cell.divID].sliceCount = slice_count;
      this.cellOverlays[cell.divID].framesCount =
        (seriesInfo && seriesInfo.numbOfFrames) ? seriesInfo.numbOfFrames: 1;

      if(this.utils.isMranoProject() && this.utils.readingId !== null) {
        // Reading Id element
        const image_reading_id_div = this.create_image_readingId_element(cell) as HTMLDivElement;
        image_reading_id_div.classList.remove('overlay-hidden');
        cell_div.appendChild(image_reading_id_div);
      }

      // Label element
      const image_label_div = this.create_image_label_element(cell) as HTMLDivElement;
      image_label_div.classList.remove('overlay-hidden');
      cell_div.appendChild(image_label_div);

      // Frame tracker element
      const frame_tracker_div = this.create_frame_tracker_element(cell) as HTMLDivElement;
      frame_tracker_div.classList.remove('overlay-hidden');
      cell_div.appendChild(frame_tracker_div);

      // Slice tracker element
      const slice_tracker_div = this.create_slice_tracker_element(cell) as HTMLDivElement;
      slice_tracker_div.classList.remove('overlay-hidden');
      cell_div.appendChild(slice_tracker_div);

      // Add a close button
      const closeBtn_div = this.create_close_btn_element(cell) as HTMLDivElement;
      cell_div.appendChild(closeBtn_div);

      // Image settings overlays
      let currentFrame: lt.Controls.Medical.Frame = cell.get_frames().get_item(cell.currentOffset);
      const im_spacing_text = 'Spacing: ('+currentFrame.get_columnSpacing().toFixed(3)+','+currentFrame.get_rowSpacing().toFixed(3)+')';
      const im_size_text = 'Image Size,px: ('+String(currentFrame.get_height())+','+String(currentFrame.get_width())+')';
      this.getOverlay(cell, 'image-spacing').set_text(im_spacing_text);
      this.getOverlay(cell, 'image-size').set_text(im_size_text);

      cell.add_sizeChanged((sender, e) => {
        // console.log('sender:', sender);
        if((<any>cell).is_deleted){
          return;
        }
        (<any>cell).sizeChangedOn = Date.now();

        if(this._parametricMaps[cell.divID]
          && this._parametricMaps[cell.divID]['mapCodeSelected']
          && this._parametricMaps[cell.divID]['mapSeriesUIDSelected'])
        {
          // redrawing map on canvas
          this.redraw_mapped_canvas(cell);

          // redraw gradient on canvas
          this.redraw_gradient_canvas(cell);
        }
      });

      cell.get_div().addEventListener('wheel', () => {
        // console.log("wheelCell cell:", cell.divID);
        this.wheelCell = cell;
      });

      // handle slice changing
      cell.add_currentOffsetChanged((e, args) => {
        // console.log('add_currentOffsetChanged...');
        this.sliceChanged.next(e);
        const current_slice_dom = document.getElementById(this.cellOverlays[cell.divID].sliceTrackerId + '_current') as HTMLInputElement;
        const prevSliceNumb = seriesInfo.prevSliceNumb ? seriesInfo.prevSliceNumb : 0;
        const curFrameNumb = seriesInfo.curFrameNumb ? seriesInfo.curFrameNumb : 1;

        const numbOfSlices = seriesInfo.numbOfSlices;
        const phantomSliceNumb = this.getRemainder(cell.currentOffset, numbOfSlices);
        const userSliceNumb = phantomSliceNumb + 1;
        if (current_slice_dom) {
          current_slice_dom.value = String(userSliceNumb);
        }

        if (this._parametricMaps[cell.divID] && this._parametricMaps[cell.divID]['mapCodeSelected']){
          // Update PM canvas on scroll
          const map_name = this._parametricMaps[cell.divID]['mapNameSelected'];
          const map_code = this._parametricMaps[cell.divID]['mapCodeSelected'];
          const seriesiuid = this._parametricMaps[cell.divID]['mapSeriesUIDSelected'];

          if (this._parametricMaps[cell.divID][map_code]
            && this._parametricMaps[cell.divID][map_code][seriesiuid]){
            // update map via canvas
            const imageMap = this._parametricMaps[cell.divID][map_code][seriesiuid]['imageDOM'][phantomSliceNumb];
            const cur_canvasElt_map = document.getElementById(cell.divID + '_pm-overlay')  as HTMLCanvasElement;
            const new_canvasElt_map = this.build_mapped_canvas(cell, imageMap, map_name);

            if (cur_canvasElt_map != null) {
              cur_canvasElt_map.style.display = 'none';
              cell_div.removeChild(cur_canvasElt_map);
            }
            new_canvasElt_map.style.display = 'block';
            new_canvasElt_map.style.opacity = (<any>cell).pm_opacity.toString();
            cell_div.appendChild(new_canvasElt_map);

            // update gradient via canvas
            const imageGradient = this._parametricMaps[cell.divID][map_code][seriesiuid]['gradientDOM'];
            const cur_canvasElt_gd = document.getElementById(cell.divID + '_gd-overlay')  as HTMLCanvasElement;
            const gd = (<any>cell).gradientData && (<any>cell).gradientData[map_name] ? (<any>cell).gradientData[map_name] : null;

            if (cur_canvasElt_gd != null && gd !== null) {
              cur_canvasElt_gd.style.display = 'none';
              cell_div.removeChild(cur_canvasElt_gd);
            }

            if (gd !== null) {
              const minVal = gd.rangeData.min;
              const maxVal = gd.rangeData.max;
              const new_canvasElt_gd = this.build_gradient_canvas(cell, imageGradient, map_name, minVal, maxVal);

              new_canvasElt_gd.style.display = 'block';
              cell_div.appendChild(new_canvasElt_gd);
            }
          }
        }
      });

      // handle pixel intensity chart
      try {
        if (this.utils.isDemriqProject()) {
          this.handle_intensity_chart(cell);
        }
      } catch (error) {

      }

      // add listener for slice input
      document.getElementById(this.cellOverlays[cell.divID].sliceTrackerIdCurrent)
      .addEventListener('change', (event: any) => {
        event.preventDefault();
        let current_slice_dom = document.getElementById(this.cellOverlays[cell.divID].sliceTrackerIdCurrent) as HTMLInputElement;
        const sliceCount = this.cellOverlays[cell.divID].sliceCount;
        const curFrameNumb = seriesInfo.curFrameNumb ? seriesInfo.curFrameNumb : 1;
        let inputOffset = null;

        if(seriesInfo.isDCE) {
          inputOffset = ((curFrameNumb - 1) * sliceCount) + parseInt(event.target.value);
        } else {
          inputOffset = parseInt(event.target.value);
        }

        if (inputOffset > 0 && (inputOffset <= sliceCount
          || (seriesInfo.isDCE && inputOffset <= curFrameNumb * sliceCount))) {
            cell.set_currentOffset(inputOffset - 1);
        } else {
            current_slice_dom.value = String(cell.get_currentOffset() % sliceCount + 1);
        }
      });

      const overlayEl = document.getElementById(this.cellOverlays[cell.divID].cellDivID);
      overlayEl.tabIndex = 0;
      overlayEl.addEventListener('keydown', (event) => {
          const current_slice_dom = document.getElementById(this.cellOverlays[cell.divID].sliceTrackerIdCurrent) as HTMLInputElement;
          const currentSlice = parseInt(current_slice_dom.value, 10);
          if (event.keyCode === DOWN_ARROW) {
              if (currentSlice < this.cellOverlays[cell.divID].sliceCount) {
                const newOffset = cell.get_currentOffset() + 1;
                current_slice_dom.value = (currentSlice + 1).toString();
                cell.set_currentOffset(newOffset);
              }
          }
          if (event.keyCode === UP_ARROW) {
              if (currentSlice > 1) {
                  const newOffset = cell.get_currentOffset() - 1;
                  current_slice_dom.value = (currentSlice - 1).toString();
                  cell.set_currentOffset(newOffset);
              }
          }
      }, {passive: true});

      // add listener for close btn
      closeBtn_div.addEventListener('click', () => {
        this.removeSeriesCellSubj.next(cell);
      });

      if (seriesInfo.isDCE) {
        let timerID = null;
        let speed = 400;
        let speedMode = 0;
        const speedLimit = [400, 200, 100, 50];
        let previousSelectedFrame;
        // create DCE layout: buttons and timeline
        const {
          timeline_id,
          nextFrameBtn,
          prevFrameBtn,
          videoPlayBtn,
          videoStopBtn,
          videoSpeedBtn,
          speedLabel_id
        } = this.createDCELayout(cell, seriesInfo);

        document.getElementById(this.cellOverlays[cell.divID].frameTrackerId + '_current')
        .addEventListener("focus", (event: any) => {
          let frameCountInput = document.getElementById(this.cellOverlays[cell.divID].frameTrackerId + '_current') as HTMLInputElement;
          previousSelectedFrame = frameCountInput.value;
        });

        // add listener for frame input
        document.getElementById(this.cellOverlays[cell.divID].frameTrackerId + '_current')
        .addEventListener('change', (event: any) => {
          event.preventDefault();
          const numbOfFrames = seriesInfo.numbOfFrames;
          const curFrameNumb = seriesInfo.curFrameNumb;
          const current_frame_dom = document.getElementById(this.cellOverlays[cell.divID].frameTrackerId + '_current') as HTMLInputElement;
          const newFrameNumb =
            (!isNaN(+current_frame_dom.value)
              && parseInt(current_frame_dom.value) > 0
              && parseInt(current_frame_dom.value) <= numbOfFrames)
              ? parseInt(current_frame_dom.value)
              : curFrameNumb;

          /**
           * This line added in order to make input frame number work well.
           * If this line be removed will affect on slice scrolling function.
          */
          seriesInfo.prevSliceNumb = null;

          previousSelectedFrame = newFrameNumb;
          // prevent out of range frame input to be applied when
          if (newFrameNumb === undefined || newFrameNumb === null) {
            this.manageFrameChange(seriesInfo, cell, this.cellOverlays[cell.divID].frameTrackerId, timeline_id, 1);
          } else {
            this.manageFrameChange(seriesInfo, cell, this.cellOverlays[cell.divID].frameTrackerId, timeline_id, newFrameNumb);
          }
        });

        // add event listener to timeline - change frame
        document.getElementById(timeline_id).addEventListener('click', (event) => {
          const frame = event.target as HTMLDivElement;
          const newFrameNumb = Number(frame.getAttribute('data-frameNumber'));

          /**
           * This line added in order to make frame navigator work well.
           * If this line be removed will affect on slice scrolling function.
          */
          seriesInfo.prevSliceNumb = null;

          this.manageFrameChange(seriesInfo, cell, this.cellOverlays[cell.divID].frameTrackerId, timeline_id, newFrameNumb);
        });

        // change frame to next or prev
        nextFrameBtn.addEventListener('click', (event) => {
          const btn = event.target as HTMLDivElement;
          const numbOfFrames = seriesInfo.numbOfFrames;
          const curFrameNumb = seriesInfo.curFrameNumb ? seriesInfo.curFrameNumb : 1;
          const newFrameNumb = (curFrameNumb < numbOfFrames)
              ? curFrameNumb + 1
              : numbOfFrames;
          this.manageFrameChange(seriesInfo, cell, this.cellOverlays[cell.divID].frameTrackerId, timeline_id, newFrameNumb);
        });

        prevFrameBtn.addEventListener("click", (event) => {
          const btn = event.target as HTMLDivElement;
          const curFrameNumb = seriesInfo.curFrameNumb ? seriesInfo.curFrameNumb : 1;
          const newFrameNumb = (curFrameNumb - 1) > 0 ? curFrameNumb - 1 : 1;
          this.manageFrameChange(seriesInfo, cell, this.cellOverlays[cell.divID].frameTrackerId, timeline_id, newFrameNumb);
        });

        const stopVideo = () => {
          clearInterval(timerID);
        }

        const playVideo = () => {
          stopVideo();
          timerID = this.playVideo(seriesInfo, cell, this.cellOverlays[cell.divID].frameTrackerId, timeline_id, speed);
          videoStopBtn.addEventListener('click', stopVideo);
        }

        videoPlayBtn.addEventListener('click', playVideo);

        videoSpeedBtn.addEventListener('click', (event) => {
          speedMode = speedMode >= 3 ? 0 : speedMode + 1;
          speed = speedLimit[speedMode];
          document.getElementById(`${cell.divID}_speed`).innerHTML = String(speedMode + 1);
          stopVideo();
          // playVideo();
        })

        this.sliceScrolling(cell, seriesInfo, timeline_id);
      } else {
        document.getElementById(this.cellOverlays[cell.divID].frameTrackerId + '_current')
        .addEventListener('keydown', (event: any) => {
          const current_frame_dom = document.getElementById(this.cellOverlays[cell.divID].frameTrackerId + '_current') as HTMLInputElement;
          current_frame_dom.value = '1';
        });
        this.sliceScrolling(cell, seriesInfo);
      }
    }

    // handle slice scrolling
    sliceScrolling(cell: lt.Controls.Medical.Cell, seriesInfo: any, timeline_id?: string): void {
      cell.add_scrollChanged((e, args) => {
        // console.log('add_scrollChanged...');
        if(this.wheelCell !== null && cell.divID === this.wheelCell.divID) {
          const numbOfSlices = seriesInfo.numbOfSlices;
          const phantomSliceNumb = this.getRemainder(cell.currentOffset, numbOfSlices);
          const prevSliceNumb = seriesInfo.prevSliceNumb;
          const userSliceNumb = phantomSliceNumb + 1;
          const curFrameNumb = seriesInfo.curFrameNumb ? seriesInfo.curFrameNumb : 1;

          // check and forbid to scroll out of the frame
          let numb = null;
          if ((phantomSliceNumb === 0) && (prevSliceNumb % (numbOfSlices - 1) === 0)) {
            numb = (userSliceNumb === 1) ? numbOfSlices - 1 : userSliceNumb - 1;
            if(seriesInfo.isDCE) {
              const numbOfFrames = seriesInfo.numbOfFrames;
              const newFrameNumb = (curFrameNumb < numbOfFrames) ? curFrameNumb + 1 : numbOfFrames;
              this.manageFrameChange(seriesInfo, cell, this.cellOverlays[cell.divID].frameTrackerId, timeline_id, newFrameNumb);
            }
          } else if(
            (phantomSliceNumb === (numbOfSlices - 1)) &&
            (prevSliceNumb !== null && prevSliceNumb % (numbOfSlices - 1) === 0) &&
            seriesInfo.isDCE) {
            const newFrameNumb = (curFrameNumb - 1) > 0 ? curFrameNumb - 1 : 1;
            this.manageFrameChange(seriesInfo, cell, this.cellOverlays[cell.divID].frameTrackerId, timeline_id, newFrameNumb);
          } else if ((phantomSliceNumb === (numbOfSlices - 1)) && (prevSliceNumb % numbOfSlices == 1)) {
            numb = (userSliceNumb == numbOfSlices) ? 1 : userSliceNumb;
            if(seriesInfo.isDCE) {
              const newFrameNumb = (curFrameNumb - 1) > 0 ? curFrameNumb - 1 : 1;
              this.manageFrameChange(seriesInfo, cell, this.cellOverlays[cell.divID].frameTrackerId, timeline_id, newFrameNumb);
            }
          }
          seriesInfo.prevSliceNumb = numb ? numb : userSliceNumb - 1;

          // ensure the rotation angle is consistent through all slices
          const frame = cell.get_frames().toArray()[cell.currentOffset];
          if ((<any>cell).user_angle && (<any>cell).user_angle !== frame.get_rotateAngle()) {
            // console.log('1.mismatch in rotation angle...');
            frame.set_rotateAngle((<any>cell).user_angle);
          }
          if ((<any>cell).user_flip && (<any>cell).user_flip !== frame.get_flipped()) {
            // console.log('1.mismatch in flip status...');
            frame.set_flipped((<any>cell).user_flip);
          }
          if ((<any>cell).user_reverse && (<any>cell).user_reverse !== frame.get_reversed()) {
            // console.log('1.mismatch in reverse status...');
            frame.set_reversed((<any>cell).user_reverse);
          }
        }
      });
    }

    handle_intensity_chart(cell: lt.Controls.Medical.Cell) {
      const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
      const numbOfSlices = seriesInfo.numbOfSlices;
      const numbOfFrames = seriesInfo.numbOfFrames;
      const cell_div = cell.get_div();
      let waitMouseEvent = false;

      // create intensity graph
      this._normalisedIntensityGraph.data = {
        names: ['crosshair'],
        times: this.getAcquisitionTimes(cell),
        crosshair: new Array(numbOfFrames).fill(0),
        xLabel: 'Norm. Intensity',
        yLabel: 'Time (ms)',
        colors: [{name: 'crosshair', value: this.getRandomColor()}]
      };

      this.drawNormalisedIntensityGraph(this._normalisedIntensityGraph.data);

      cell_div.addEventListener('mousemove', throttle((event) => {
        const cell_el = cell.get_div();
        const cell_H = cell_el.clientHeight;
        const cell_W = cell_el.clientWidth;
        const cur_x = event.offsetX;
        const cur_y = event.offsetY;

        const cellFrame: lt.Controls.Medical.Frame = cell.get_frames().get_item(cell.get_currentOffset());
        if (cellFrame === null || cellFrame === undefined) {
          return;
        }
        const im_h = cellFrame.get_height();
        const im_w = cellFrame.get_width();

        let bg_h, bg_w;
        if (im_h === im_w) {
          bg_h = (cell_H < cell_W) ? cell_H : cell_W;
          bg_w = bg_h;
        } else {
          bg_h = cell_H;
          bg_w = cell_W;
        }
        const mg_x = (cell_W - bg_h) / 2;
        const mg_y = (cell_H - bg_w) / 2;
        const rel_x = Math.round(((cur_x - mg_x) * im_w) / bg_w);
        const rel_y = Math.round(((cur_y - mg_y) * im_h) / bg_h);
        const rgbValue = lt.Controls.Medical.ProbeToolInteractiveMode.getPixelValue(cellFrame, rel_x, rel_y);
        const viewerComponent = seriesInfo.viewerComponent;
        const isIntChartPanel = viewerComponent.viewerSettings.toolbar.isIntChartPanel;

        const roiInfo = viewerComponent.selection1.selected;

        if (roiInfo && isIntChartPanel && viewerComponent.splitConfig && !viewerComponent.splitConfig.disabled && rgbValue !== null) {
          roiInfo.forEach((roi) => {
            const normROIPixelValues = this.getROINormalizedPixelValuesArray(cell, numbOfSlices, numbOfFrames, roi);
            normROIPixelValues.then((roiData) => {
              this.addRowDataForGraph(roi, roiData);
            });
          });
        }

        const voiInfo = viewerComponent.selection2.selected;
        if (voiInfo && isIntChartPanel && viewerComponent.splitConfig && !viewerComponent.splitConfig.disabled && rgbValue !== null) {
          voiInfo.forEach((voi) => {
            const normVOIPixelValues = this.getVOINormalizedPixelValuesArray(cell, numbOfSlices, numbOfFrames, voi);
            normVOIPixelValues.then((voiData) => {
              this.addRowDataForGraph(voi, voiData);
            });
          });
        }

        this._normalisedIntensityGraph.data.names.forEach(plotName => {
          const roiIndexUnchecked = roiInfo.map(r => r.name).indexOf(plotName);
          const voiIndexUnchecked = voiInfo.map(v => v.name).indexOf(plotName);
          if (roiIndexUnchecked === -1 && voiIndexUnchecked === -1 && plotName !== 'crosshair') {
            this._normalisedIntensityGraph.data.names.splice(
              this._normalisedIntensityGraph.data.names.indexOf(plotName), 1);
          }
        });

        if (isIntChartPanel && viewerComponent.splitConfig && !viewerComponent.splitConfig.disabled && rgbValue !== null) {
          const normPixelValues = this.getNormalizedPixelValuesArray(cell, rel_x, rel_y, numbOfSlices, numbOfFrames);
          normPixelValues.then((data) => {
            this._normalisedIntensityGraph.data.crosshair = data;
            if (this._normalisedIntensityGraph.data.crosshair !== null) {
              this.drawNormalisedIntensityGraph(this._normalisedIntensityGraph.data);
            }
          });
        }
      }, 500));
    }

    addRowDataForGraph(row: any, rowData: any) {
      if (!this._normalisedIntensityGraph.data.names.includes(row.name)) {
        this._normalisedIntensityGraph.data.names.push(row.name);
        this._normalisedIntensityGraph.data.colors.push({name: row.name, value: this.getRandomColor()});
      }
      this._normalisedIntensityGraph.data[row.name] = rowData;
    }

    getRandomColor(): any {
      const x = Math.floor(Math.random() * 250);
      const y = Math.floor(Math.random() * 250);
      const z = Math.floor(Math.random() * 250);
      return 'rgb(' + x + ',' + y + ',' + z + ')';
    }

    create_image_readingId_element(cell) {
      const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
      let imageReadingIdDiv = document.createElement('div');
      let imageReadingId = cell.divID+"_image-ReadingId";
      let imageReadingIdCurrent = "Reading ID: " + this.utils.readingId;

      let imageLabelStr = "<div id='"+imageReadingId+"' class='overlay-hidden' style='position:absolute; z-index:100; top:60px; left:10px; color:white; font-size: 0.90rem;'>";
      imageLabelStr += imageReadingIdCurrent + "</div>";

      imageReadingIdDiv.innerHTML = imageLabelStr.trim();
      return imageReadingIdDiv.firstChild;
    }

    create_image_label_element(cell) {
      const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
      let imageLabelDiv = document.createElement('div');
      let imageLabelId = cell.divID+"_image-label";
      let imageLabelIdCurrent = "Label: " + seriesInfo.label;
      const top = this.utils.isMranoProject() && this.utils.readingId !== null ? '90' : '60';
      let imageLabelStr = "<div id='"+imageLabelId+"' class='overlay-hidden' style='position:absolute; z-index:100; top:"+top+"px; left:10px; color:white; font-size: 0.90rem;'>";
      imageLabelStr += imageLabelIdCurrent + "</div>";

      imageLabelDiv.innerHTML = imageLabelStr.trim();
      return imageLabelDiv.firstChild;
    }

    create_frame_tracker_element(cell) {
      let frameTrackerDiv = document.createElement('div');
      let frameTrackerId = cell.divID+"_frame-tracker";
      let frameTrackerIdCurrent = cell.divID + "_frame-tracker_current";
      let frameTrackerIdCount = cell.divID + "_frame-tracker_count";
      let frameTrackerIdBegtime = cell.divID + "_frame-tracker_begtime";
      let frameTrackerIdBegtimeVal = cell.divID + "_frame-tracker_begtime_val";
      const top = this.utils.isMranoProject() && this.utils.readingId !== null ? '120' : '90';
      let frameTrackerStr = "<div id='"+frameTrackerId+"' class='overlay-hidden' style='position:absolute; z-index:100; top:"+top+"px; left:10px; color:white; font-size: 0.90rem;'>";
      frameTrackerStr += "Frame: <input type='text' id='"+frameTrackerIdCurrent+"' value='1' style='display: inline;width: 35px;height: calc(100% - 1px);text-align: center;'/> / ";

      if(this.cellOverlays[cell.divID].framesCount > 1) {
        frameTrackerStr += "<span id='"+frameTrackerIdCount+"'>"+this.cellOverlays[cell.divID].framesCount+"</span><span id='"+frameTrackerIdBegtime+"' class='custom-overlay__begTime'>(<span id='"+frameTrackerIdBegtimeVal+"'>0</span> sec)</span></div>";
      } else {
        frameTrackerStr += "<span id='"+frameTrackerIdCount+"'>"+this.cellOverlays[cell.divID].framesCount+"</span><span id='"+frameTrackerIdBegtime+"' class='custom-overlay__begTime' style='display:none'>(<span id='"+frameTrackerIdBegtimeVal+"'>0</span> sec)</span></div>";
      }

      frameTrackerDiv.innerHTML = frameTrackerStr.trim();
      return frameTrackerDiv.firstChild;
    }

    create_slice_tracker_element(cell) {
      let sliceTrackerDiv = document.createElement('div');
      let sliceTrackerId = cell.divID+"_slice-tracker";
      let sliceTrackerIdCurrent = cell.divID + "_slice-tracker_current";
      let sliceTrackerIdCount = cell.divID + "_slice-tracker_count";
      const top = this.utils.isMranoProject() && this.utils.readingId !== null ? '150' : '120';
      let sliceTrackerStr = "<div id='"+sliceTrackerId+"' class='overlay-hidden' style='position:absolute; z-index:100; top:"+top+"px; left:10px; color:white; font-size: 0.90rem'>";
      sliceTrackerStr += "Slice: <input type='text' id='"+sliceTrackerIdCurrent+"' value='1' style='margin-left: 10px;display: inline;width: 35px;height: calc(100% - 1px);text-align: center;'/> / ";
      sliceTrackerStr += "<span id='"+sliceTrackerIdCount+"'>"+this.cellOverlays[cell.divID].sliceCount+"</span></div>";

      sliceTrackerDiv.innerHTML = sliceTrackerStr.trim();
      return sliceTrackerDiv.firstChild;
    }

    create_close_btn_element(cell){
        let closeBtnDiv = document.createElement('div') as HTMLDivElement;
        closeBtnDiv.setAttribute("id", cell.divID+"_closeBtn");
        closeBtnDiv.className = 'viewerCloseBtn';
        return closeBtnDiv;
    }

    create_opacity_slicer_element(cell){
      const opacitySlicerDiv = document.createElement('div');
      const opacitySlicerId = cell.divID+"_opacity-slicer";
      opacitySlicerDiv.className="opacity-slicer";
      const opacitySlicerStr = "<span>Transparency:</span><input type='range' min='0' max='1' step='0.1' id='"+opacitySlicerId+"'>";
      opacitySlicerDiv.innerHTML = opacitySlicerStr.trim();
      return opacitySlicerDiv;
    }

    /**
     * Apply colormap to data.
     * @param {Object} data image RGBA data array
     * @param {Object} colorMap RGBA data array
     */

    applyColormap(data, colorMap, isNewVersion?, mapName?) {
        /**
        * Maps from LeadTools are stored in bluescale,
        * therefore, we only map the blue values from
        * the RGBA data.
        */
      if(isNewVersion != undefined && isNewVersion === true) {
        if (mapName === 'FTB Reg') {
          for (let i = 0; i < data.length; i += 4) {
            const blueValue = data[i+2];
            data[i] = colorMap[(blueValue * 4) + 1];
            data[i+1] = colorMap[(blueValue * 4) + 2];
            data[i+2] = colorMap[(blueValue * 4) + 3];
            if((data[i] === 0 && data[i+1] === 0 && data[i+2] === 0) || (data[i] === 231 && data[i+1] === 243 && data[i+2] === 155) || blueValue < 2) {
              data[i+3] = 0;
            } else {
              data[i+3] = 255;
            }
          }
        } else {
          for (let i = 0; i < data.length; i += 4) {
            const blueValue = data[i+2];
            data[i] = colorMap[(blueValue * 4) + 1];
            data[i+1] = colorMap[(blueValue * 4) + 2];
            data[i+2] = colorMap[(blueValue * 4) + 3];
            if(blueValue !== 0) {
              data[i+3] = 255;
            } else {
              data[i+3] = blueValue;
            }
          }
        }
      } else {
        // Map RGBA values to colormap
        for (let i = 0; i < data.length; i += 4) {
            const blueValue = data[i+2];
            data[i] = colorMap[blueValue].r;
            data[i+1] = colorMap[blueValue].g;
            data[i+2] = colorMap[blueValue].b;
            data[i+3] = colorMap[blueValue].a;
        }
      }
    }

    /**
     * Generates Yellow Red colormap.
     * @return {Object} colorMap RGBA data array
     */
    getYellowRedColorMap() {
        const colorMap = [];
        const maxValue = 255;
        for (let i = 0; i <= maxValue; i++) {
            colorMap[i] = {
                r: Math.min(i * 3, maxValue),
                g: Math.min(Math.max(0, (i - (maxValue/3)) * 3), maxValue),
                b: Math.min(Math.max(0, (i - (maxValue*2/3)) * 3), maxValue),
                a: maxValue
            }
        }
        colorMap[0].a = 0; //make zero values invisible
        return colorMap;
    }

    /**
     * Generates Blue Violet colormap.
     * @return {Object} colorMap RGBA data array
     */
    getBlueVioletColorMap() {
        const colorMap = [];
        const maxValue = 255;
        for (let i = 0; i <= maxValue; i++) {
            colorMap[i] = {
                r: Math.min(Math.max(0, (i - (maxValue/3)) * 3), maxValue),
                g: Math.min(Math.max(0, (i - (maxValue*2/3)) * 3), maxValue),
                b: Math.min(i * 3, maxValue),
                a: maxValue
            }
        }
        colorMap[0].a = 0; //make zero values invisible
        return colorMap;
    }

    /**
     * Generates Green Blue colormap.
     * @return {Object} colorMap RGBA data array
     */
    getGreenBlueColorMap() {
        const colorMap = [];
        const maxValue = 255;
        for (let i = 0; i <= maxValue; i++) {
            colorMap[i] = {
                r: Math.min(Math.max(0, (i - (maxValue*2/3)) * 3), maxValue),
                g: Math.min(i * 3, maxValue),
                b: Math.min(Math.max(0, (i - (maxValue/3)) * 3), maxValue),
                a: maxValue
            }
        }
        colorMap[0].a = 0; //make zero values invisible
        return colorMap;
    }

    /**
     * Generates Jet colormap.
     * @return {Object} colorMap RGBA data array
     */
    getJetColorMap() {
        const colorMap = [];
        const maxValue = 255;
        const size = 256;
        const r = [];
        const g = [];
        const b = [];
        for (let i = 0; i <= maxValue; i++) {
            r[i] = 0;
            g[i] = 0;
            b[i] = 0;
        }
        g.fill(0, 0, size / 8);
        for (let i = 0; i < size/4; i++) {
            g[i+size/8] = maxValue*i*4/size;
        }
        g.fill(maxValue, size*3/8, size*5/8);
        for (let i = 0; i < size/4; i++) {
            g[i+size*5/8] = maxValue - (maxValue*i*4/size);
        }
        g.fill(0, size*7/8, size);
        for (let i = 0; i < g.length; i++) {
            b[i] = g[(i+size/4) % g.length];
        }
        b.fill(0, size*7/8, size);
        g.fill(0, 0, size/8);
        for(let i = size/8; i < g.length; i++) {
            r[i] = g[(i+size*6/8) % g.length];
        }
        for (let i = 0; i <= maxValue; i++) {
            colorMap[i] = {
                r: r[size-1-i],
                g: g[size-1-i],
                b: b[size-1-i],
                a: maxValue
            }
        }
        colorMap[0].a = 0; //make zero values invisible
        return colorMap;
    }

    /**
     * Generates Jet colormap.
     * @return {Object} colorMap RGBA data array
     */
    getrCBVColorMap() {
      const colorMap = [0,4,1,4,1,4,1,4,2,7,1,8,3,9,1,13,4,10,1,17,5,13,1,21,6,16,1,25,7,18,1,30,8,19,1,34,9,22,1,38,10,25,1,42,11,27,1,47,12,28,1,51,13,31,1,55,14,34,1,59,15,35,1,64,16,38,1,67,17,41,1,71,18,43,1,75,19,44,1,80,20,47,1,84,21,50,1,88,22,52,1,92,23,53,1,97,24,56,1,101,25,59,1,105,26,61,1,109,27,62,1,114,28,64,1,118,29,67,1,122,30,68,1,127,31,68,1,127,32,67,1,131,33,64,1,135,34,62,1,139,35,61,1,143,36,59,1,147,37,56,1,151,38,53,1,155,39,52,1,159,40,50,1,163,41,47,1,167,42,44,1,171,43,43,1,175,44,41,1,179,45,38,1,183,46,35,1,187,47,34,1,191,48,31,1,194,49,28,1,198,50,27,1,202,51,25,1,206,52,22,1,210,53,19,1,214,54,18,1,218,55,16,1,222,56,13,1,226,57,10,1,230,58,9,1,234,59,7,1,238,60,4,1,242,61,2,1,246,62,1,1,254,63,1,1,254,64,1,5,246,65,1,10,242,66,1,14,238,67,1,19,234,68,1,23,230,69,1,28,226,70,1,32,222,71,1,37,218,72,1,41,214,73,1,46,210,74,1,50,206,75,1,55,202,76,1,59,198,77,1,64,194,78,1,67,191,79,1,72,187,80,1,76,183,81,1,81,179,82,1,85,175,83,1,90,171,84,1,94,167,85,1,99,163,86,1,103,159,87,1,108,155,88,1,112,151,89,1,117,147,90,1,121,143,91,1,126,139,92,1,130,135,93,1,135,131,94,1,139,127,95,1,144,122,96,1,148,118,97,1,153,114,98,1,157,109,99,1,162,105,100,1,166,101,101,1,171,97,102,1,175,92,103,1,180,88,104,1,184,84,105,1,189,80,106,1,192,75,107,1,203,71,108,1,203,67,109,10,205,64,110,19,207,59,111,28,209,55,112,37,211,51,113,46,213,47,114,55,215,42,115,64,217,38,116,72,219,34,117,81,221,30,118,90,223,25,119,99,225,21,120,108,227,17,121,117,229,13,122,126,231,11,123,135,233,8,124,144,235,5,125,153,237,2,126,162,239,1,127,171,241,1,128,180,242,1,129,189,244,1,130,197,245,1,131,206,247,1,132,215,248,1,133,224,250,1,134,233,251,1,135,242,253,1,136,254,254,1,137,254,254,1,138,254,253,1,139,254,251,1,140,254,249,1,141,254,247,1,142,254,245,1,143,254,243,1,144,254,241,1,145,254,239,1,146,254,237,1,147,254,235,1,148,254,233,1,149,254,231,1,150,254,229,1,151,254,227,1,152,254,225,1,153,254,223,1,154,254,221,1,155,254,219,1,156,254,217,1,157,254,215,1,158,254,213,1,159,254,211,1,160,254,209,1,161,254,207,1,162,254,205,1,163,254,203,1,164,254,201,1,165,254,199,1,166,254,197,1,167,254,195,1,168,254,193,1,169,254,191,1,170,254,190,1,171,254,188,1,172,254,186,1,173,254,184,1,174,254,182,1,175,254,180,1,176,254,178,1,177,254,176,1,178,254,174,1,179,254,172,1,180,254,170,1,181,254,168,1,182,254,166,1,183,254,164,1,184,254,162,1,185,254,160,1,186,254,158,1,187,254,156,1,188,254,154,1,189,254,152,1,190,254,150,1,191,254,148,1,192,254,146,1,193,254,144,1,194,254,142,1,195,254,140,1,196,254,138,1,197,254,136,1,198,254,134,1,199,254,132,1,200,254,130,1,201,254,128,1,202,254,126,1,203,254,124,1,204,254,122,1,205,254,120,1,206,254,118,1,207,254,116,1,208,254,114,1,209,254,112,1,210,254,110,1,211,254,108,1,212,254,106,1,213,254,104,1,214,254,102,1,215,254,100,1,216,254,98,1,217,254,96,1,218,254,94,1,219,254,92,1,220,254,90,1,221,254,88,1,222,254,86,1,223,254,84,1,224,254,82,1,225,254,80,1,226,254,78,1,227,254,76,1,228,254,74,1,229,254,72,1,230,254,69,1,231,254,66,1,232,254,64,1,233,254,62,1,234,254,59,1,235,254,56,1,236,254,54,1,237,254,51,1,238,254,48,1,239,254,45,1,240,254,43,1,241,254,40,1,242,254,37,1,243,254,34,1,244,254,32,1,245,254,29,1,246,254,26,1,247,254,23,1,248,254,21,1,249,254,18,1,250,254,15,1,251,254,12,1,252,254,10,1,253,254,7,1,254,254,4,1,255,254,4,1];
      return colorMap;
    }

    /**
     * Generates Ian colormap.
     * @return {Object} colorMap RGBA data array
     */
    getADCColorMap() {
      //Colorful
      // const colorMap = [0,1,1,7,1,1,1,7,2,1,1,13,3,1,1,18,4,1,1,24,5,1,1,30,6,1,1,36,7,1,1,42,8,1,1,47,9,1,1,53,10,1,1,59,11,1,1,64,12,1,1,70,13,1,1,75,14,1,1,81,15,1,1,85,16,1,1,89,17,1,1,93,18,1,1,98,19,1,1,104,20,1,1,109,21,1,1,115,22,1,1,121,23,1,1,126,24,1,1,132,25,1,1,137,26,1,1,143,27,1,1,149,28,1,1,154,29,1,1,160,30,1,1,165,31,1,1,169,32,1,1,174,33,1,7,177,34,1,13,182,35,1,18,188,36,1,24,192,37,1,30,198,38,1,36,204,39,1,42,209,40,1,47,215,41,1,53,220,42,1,59,226,43,1,64,232,44,1,70,237,45,1,75,243,46,1,81,248,47,1,85,254,48,1,89,254,49,1,93,254,50,1,98,254,51,1,104,254,52,1,109,254,53,1,115,254,54,1,121,254,55,1,126,254,56,1,132,254,57,1,137,254,58,1,143,254,59,1,149,254,60,1,154,254,61,1,160,254,62,1,165,254,63,1,169,254,64,1,174,254,65,1,177,254,66,1,182,254,67,1,188,254,68,1,192,254,69,1,198,254,70,1,204,254,71,1,209,254,72,1,215,254,73,1,220,254,74,1,226,254,75,1,232,254,76,1,237,254,77,1,243,254,78,1,248,254,79,1,254,254,80,1,254,254,81,1,254,254,82,1,254,254,83,1,254,254,84,1,254,254,85,1,254,254,86,1,254,254,87,1,254,254,88,1,254,254,89,1,254,254,90,1,254,254,91,1,254,254,92,1,254,254,93,1,254,254,94,1,254,254,95,1,254,254,96,1,254,238,97,10,254,222,98,18,254,206,99,27,254,191,100,36,254,175,101,45,254,159,102,53,254,143,103,62,254,127,104,70,254,111,105,79,254,95,106,87,254,79,107,96,254,64,108,105,254,48,109,114,254,32,110,122,254,16,111,128,254,1,112,134,254,1,113,139,252,1,114,148,250,1,115,156,248,1,116,164,245,1,117,172,243,1,118,181,241,1,119,189,239,1,120,196,237,1,121,204,235,1,122,213,233,1,123,221,231,1,124,229,228,1,125,237,226,1,126,246,224,1,127,254,222,1,128,254,222,1,129,254,219,1,130,254,217,1,131,254,214,1,132,254,211,1,133,254,209,1,134,254,206,1,135,254,203,1,136,254,201,1,137,254,198,1,138,254,195,1,139,254,193,1,140,254,191,1,141,254,188,1,142,254,186,1,143,254,184,1,144,254,182,1,145,254,180,1,146,254,176,1,147,254,173,1,148,254,169,1,149,254,166,1,150,254,162,1,151,254,159,1,152,254,155,1,153,254,152,1,154,254,148,1,155,254,145,1,156,254,141,1,157,254,138,1,158,254,134,1,159,254,129,1,160,254,131,1,161,254,127,1,162,254,122,1,163,254,118,1,164,254,114,1,165,254,110,1,166,254,105,1,167,254,101,1,168,254,97,1,169,254,93,1,170,254,88,1,171,254,84,1,172,254,80,1,173,254,76,1,174,254,71,1,175,254,66,1,176,254,68,1,177,254,64,1,178,254,59,1,179,254,55,1,180,254,50,1,181,254,46,1,182,254,41,1,183,254,37,1,184,254,32,1,185,254,28,1,186,254,23,1,187,254,19,1,188,254,14,1,189,254,10,1,190,254,5,1,191,254,1,1,192,254,1,1,193,252,14,14,194,250,26,26,195,248,39,39,196,245,52,52,197,243,64,64,198,241,76,76,199,239,89,89,200,237,102,102,201,235,115,115,202,233,127,127,203,231,140,140,204,228,153,153,205,226,166,166,206,224,178,178,207,222,183,183,208,222,188,188,209,222,192,192,210,222,194,194,211,222,196,196,212,222,199,199,213,222,201,201,214,222,203,203,215,222,205,205,216,222,207,207,217,222,209,209,218,222,211,211,219,222,213,213,220,222,216,216,221,222,218,218,222,222,220,220,223,222,222,222,224,222,222,222,225,224,224,224,226,226,226,226,227,228,228,228,228,231,231,231,229,233,233,233,230,235,235,235,231,237,237,237,232,239,239,239,233,241,241,241,234,243,243,243,235,245,245,245,236,248,248,248,237,250,250,250,238,252,252,252,239,254,254,254,240,254,254,254,241,254,254,254,242,254,254,254,243,254,254,254,244,254,254,254,245,254,254,254,246,254,254,254,247,254,254,254,248,254,254,254,249,254,254,254,250,254,254,254,251,254,254,254,252,254,254,254,253,254,254,254,254,254,254,254,255,254,254,254];
      // Gray 1
      const colorMap = [0,0,0,0,1,1,1,1,2,2,2,2,3,3,3,3,4,4,4,4,5,5,5,5,6,6,6,6,7,7,7,7,8,8,8,8,9,9,9,9,10,10,10,10,11,11,11,11,12,12,12,12,13,13,13,13,14,14,14,14,15,15,15,15,16,16,16,16,17,17,17,17,18,18,18,18,19,19,19,19,20,20,20,20,21,21,21,21,22,22,22,22,23,23,23,23,24,24,24,24,25,25,25,25,26,26,26,26,27,27,27,27,28,28,28,28,29,29,29,29,30,30,30,30,31,31,31,31,32,32,32,32,33,33,33,33,34,34,34,34,35,35,35,35,36,36,36,36,37,37,37,37,38,38,38,38,39,39,39,39,40,40,40,40,41,41,41,41,42,42,42,42,43,43,43,43,44,44,44,44,45,45,45,45,46,46,46,46,47,47,47,47,48,48,48,48,49,49,49,49,50,50,50,50,51,51,51,51,52,52,52,52,53,53,53,53,54,54,54,54,55,55,55,55,56,56,56,56,57,57,57,57,58,58,58,58,59,59,59,59,60,60,60,60,61,61,61,61,62,62,62,62,63,63,63,63,64,64,64,64,65,65,65,65,66,66,66,66,67,67,67,67,68,68,68,68,69,69,69,69,70,70,70,70,71,71,71,71,72,72,72,72,73,73,73,73,74,74,74,74,75,75,75,75,76,76,76,76,77,77,77,77,78,78,78,78,79,79,79,79,80,80,80,80,81,81,81,81,82,82,82,82,83,83,83,83,84,84,84,84,85,85,85,85,86,86,86,86,87,87,87,87,88,88,88,88,89,89,89,89,90,90,90,90,91,91,91,91,92,92,92,92,93,93,93,93,94,94,94,94,95,95,95,95,96,96,96,96,97,97,97,97,98,98,98,98,99,99,99,99,100,100,100,100,101,101,101,101,102,102,102,102,103,103,103,103,104,104,104,104,105,105,105,105,106,106,106,106,107,107,107,107,108,108,108,108,109,109,109,109,110,110,110,110,111,111,111,111,112,112,112,112,113,113,113,113,114,114,114,114,115,115,115,115,116,116,116,116,117,117,117,117,118,118,118,118,119,119,119,119,120,120,120,120,121,121,121,121,122,122,122,122,123,123,123,123,124,124,124,124,125,125,125,125,126,126,126,126,127,127,127,127,128,128,128,128,129,129,129,129,130,130,130,130,131,131,131,131,132,132,132,132,133,133,133,133,134,134,134,134,135,135,135,135,136,136,136,136,137,137,137,137,138,138,138,138,139,139,139,139,140,140,140,140,141,141,141,141,142,142,142,142,143,143,143,143,144,144,144,144,145,145,145,145,146,146,146,146,147,147,147,147,148,148,148,148,149,149,149,149,150,150,150,150,151,151,151,151,152,152,152,152,153,153,153,153,154,154,154,154,155,155,155,155,156,156,156,156,157,157,157,157,158,158,158,158,159,159,159,159,160,160,160,160,161,161,161,161,162,162,162,162,163,163,163,163,164,164,164,164,165,165,165,165,166,166,166,166,167,167,167,167,168,168,168,168,169,169,169,169,170,170,170,170,171,171,171,171,172,172,172,172,173,173,173,173,174,174,174,174,175,175,175,175,176,176,176,176,177,177,177,177,178,178,178,178,179,179,179,179,180,180,180,180,181,181,181,181,182,182,182,182,183,183,183,183,184,184,184,184,185,185,185,185,186,186,186,186,187,187,187,187,188,188,188,188,189,189,189,189,190,190,190,190,191,191,191,191,192,192,192,192,193,193,193,193,194,194,194,194,195,195,195,195,196,196,196,196,197,197,197,197,198,198,198,198,199,199,199,199,200,200,200,200,201,201,201,201,202,202,202,202,203,203,203,203,204,204,204,204,205,205,205,205,206,206,206,206,207,207,207,207,208,208,208,208,209,209,209,209,210,210,210,210,211,211,211,211,212,212,212,212,213,213,213,213,214,214,214,214,215,215,215,215,216,216,216,216,217,217,217,217,218,218,218,218,219,219,219,219,220,220,220,220,221,221,221,221,222,222,222,222,223,223,223,223,224,224,224,224,225,225,225,225,226,226,226,226,227,227,227,227,228,228,228,228,229,229,229,229,230,230,230,230,231,231,231,231,232,232,232,232,233,233,233,233,234,234,234,234,235,235,235,235,236,236,236,236,237,237,237,237,238,238,238,238,239,239,239,239,240,240,240,240,241,241,241,241,242,242,242,242,243,243,243,243,244,244,244,244,245,245,245,245,246,246,246,246,247,247,247,247,248,248,248,248,249,249,249,249,250,250,250,250,251,251,251,251,252,252,252,252,253,253,253,253,254,254,254,254,255,255,255,255];
      // Gray 2
      // const colorMap = [0,255,255,255,1,254,254,254,2,253,253,253,3,252,252,252,4,251,251,251,5,250,250,250,6,249,249,249,7,248,248,248,8,247,247,247,9,246,246,246,10,245,245,245,11,244,244,244,12,243,243,243,13,242,242,242,14,241,241,241,15,240,240,240,16,239,239,239,17,238,238,238,18,237,237,237,19,236,236,236,20,235,235,235,21,234,234,234,22,233,233,233,23,232,232,232,24,231,231,231,25,230,230,230,26,229,229,229,27,228,228,228,28,227,227,227,29,226,226,226,30,225,225,225,31,224,224,224,32,223,223,223,33,222,222,222,34,221,221,221,35,220,220,220,36,219,219,219,37,218,218,218,38,217,217,217,39,216,216,216,40,215,215,215,41,214,214,214,42,213,213,213,43,212,212,212,44,211,211,211,45,210,210,210,46,209,209,209,47,208,208,208,48,207,207,207,49,206,206,206,50,205,205,205,51,204,204,204,52,203,203,203,53,202,202,202,54,201,201,201,55,200,200,200,56,199,199,199,57,198,198,198,58,197,197,197,59,196,196,196,60,195,195,195,61,194,194,194,62,193,193,193,63,192,192,192,64,191,191,191,65,190,190,190,66,189,189,189,67,188,188,188,68,187,187,187,69,186,186,186,70,185,185,185,71,184,184,184,72,183,183,183,73,182,182,182,74,181,181,181,75,180,180,180,76,179,179,179,77,178,178,178,78,177,177,177,79,176,176,176,80,175,175,175,81,174,174,174,82,173,173,173,83,172,172,172,84,171,171,171,85,170,170,170,86,169,169,169,87,168,168,168,88,167,167,167,89,166,166,166,90,165,165,165,91,164,164,164,92,163,163,163,93,162,162,162,94,161,161,161,95,160,160,160,96,159,159,159,97,158,158,158,98,157,157,157,99,156,156,156,100,155,155,155,101,154,154,154,102,153,153,153,103,152,152,152,104,151,151,151,105,150,150,150,106,149,149,149,107,148,148,148,108,147,147,147,109,146,146,146,110,145,145,145,111,144,144,144,112,143,143,143,113,142,142,142,114,141,141,141,115,140,140,140,116,139,139,139,117,138,138,138,118,137,137,137,119,136,136,136,120,135,135,135,121,134,134,134,122,133,133,133,123,132,132,132,124,131,131,131,125,130,130,130,126,129,129,129,127,128,128,128,128,127,127,127,129,126,126,126,130,125,125,125,131,124,124,124,132,123,123,123,133,122,122,122,134,121,121,121,135,120,120,120,136,119,119,119,137,118,118,118,138,117,117,117,139,116,116,116,140,115,115,115,141,114,114,114,142,113,113,113,143,112,112,112,144,111,111,111,145,110,110,110,146,109,109,109,147,108,108,108,148,107,107,107,149,106,106,106,150,105,105,105,151,104,104,104,152,103,103,103,153,102,102,102,154,101,101,101,155,100,100,100,156,99,99,99,157,98,98,98,158,97,97,97,159,96,96,96,160,95,95,95,161,94,94,94,162,93,93,93,163,92,92,92,164,91,91,91,165,90,90,90,166,89,89,89,167,88,88,88,168,87,87,87,169,86,86,86,170,85,85,85,171,84,84,84,172,83,83,83,173,82,82,82,174,81,81,81,175,80,80,80,176,79,79,79,177,78,78,78,178,77,77,77,179,76,76,76,180,75,75,75,181,74,74,74,182,73,73,73,183,72,72,72,184,71,71,71,185,70,70,70,186,69,69,69,187,68,68,68,188,67,67,67,189,66,66,66,190,65,65,65,191,64,64,64,192,63,63,63,193,62,62,62,194,61,61,61,195,60,60,60,196,59,59,59,197,58,58,58,198,57,57,57,199,56,56,56,200,55,55,55,201,54,54,54,202,53,53,53,203,52,52,52,204,51,51,51,205,50,50,50,206,49,49,49,207,48,48,48,208,47,47,47,209,46,46,46,210,45,45,45,211,44,44,44,212,43,43,43,213,42,42,42,214,41,41,41,215,40,40,40,216,39,39,39,217,38,38,38,218,37,37,37,219,36,36,36,220,35,35,35,221,34,34,34,222,33,33,33,223,32,32,32,224,31,31,31,225,30,30,30,226,29,29,29,227,28,28,28,228,27,27,27,229,26,26,26,230,25,25,25,231,24,24,24,232,23,23,23,233,22,22,22,234,21,21,21,235,20,20,20,236,19,19,19,237,18,18,18,238,17,17,17,239,16,16,16,240,15,15,15,241,14,14,14,242,13,13,13,243,12,12,12,244,11,11,11,245,10,10,10,246,9,9,9,247,8,8,8,248,7,7,7,249,6,6,6,250,5,5,5,251,4,4,4,252,3,3,3,253,2,2,2,254,1,1,1,255,0,0,0];
      return colorMap;
    }

    /**
     * Generates Sopha colormap.
     * @return {Object} colorMap RGBA data array
     */
    getToftsVEColorMap() {
      const colorMap = [0,1,1,105,1,1,1,105,2,1,1,110,3,1,1,115,4,1,1,120,5,1,1,125,6,1,1,129,7,1,1,134,8,1,1,139,9,1,1,144,10,1,1,149,11,1,1,153,12,1,1,158,13,1,1,163,14,1,1,168,15,1,1,173,16,1,1,178,17,1,1,182,18,1,1,187,19,1,9,191,20,1,17,196,21,1,25,201,22,1,33,205,23,1,41,210,24,1,49,215,25,1,57,220,26,1,65,225,27,1,73,229,28,1,81,234,29,1,89,239,30,1,97,244,31,1,105,249,32,1,113,254,33,1,121,254,34,1,121,250,35,1,121,246,36,1,121,242,37,1,121,238,38,1,121,234,39,1,121,230,40,1,121,226,41,1,121,222,42,1,121,218,43,1,121,214,44,1,121,210,45,1,121,206,46,1,121,202,47,1,121,198,48,1,121,194,49,1,121,191,50,1,121,187,51,1,128,183,52,1,135,179,53,1,142,175,54,1,149,171,55,1,155,167,56,1,162,163,57,1,169,159,58,1,176,155,59,1,183,151,60,1,190,147,61,1,195,143,62,1,202,139,63,1,209,135,64,1,216,131,65,1,223,128,66,1,223,118,67,1,223,109,68,1,223,100,69,1,223,91,70,1,223,82,71,1,223,73,72,1,223,64,73,1,223,55,74,1,223,46,75,1,223,37,76,1,223,28,77,1,223,19,78,1,223,10,79,1,223,1,80,1,223,1,81,1,223,1,82,1,223,1,83,1,225,1,84,1,227,1,85,1,229,1,86,1,231,1,87,1,233,1,88,1,235,1,89,1,237,1,90,1,239,1,91,1,241,1,92,1,243,1,93,1,245,1,94,1,247,1,95,1,249,1,96,1,251,1,97,1,254,1,98,8,254,1,99,16,254,1,100,24,254,1,101,32,254,1,102,40,254,6,103,48,254,12,104,56,254,17,105,64,254,23,106,71,254,28,107,79,254,34,108,87,254,40,109,95,254,45,110,103,254,51,111,111,254,56,112,119,254,62,113,127,254,66,114,135,254,66,115,143,251,66,116,151,249,66,117,159,247,66,118,167,245,66,119,175,243,66,120,183,241,66,121,191,239,66,122,198,237,66,123,206,235,66,124,214,233,66,125,222,231,66,126,230,229,66,127,238,227,66,128,246,225,66,129,254,223,66,130,254,223,62,131,254,223,56,132,254,223,51,133,254,223,45,134,254,223,40,135,254,223,34,136,254,223,28,137,254,223,23,138,254,223,17,139,254,223,12,140,254,223,6,141,254,223,1,142,254,223,1,143,254,223,1,144,254,223,1,145,254,223,1,146,254,223,1,147,248,211,1,148,243,200,1,149,237,189,1,150,232,178,1,151,226,166,1,152,221,155,1,153,215,143,1,154,210,132,1,155,205,120,1,156,205,109,1,157,205,97,1,158,205,86,1,159,205,74,1,160,205,64,1,161,205,52,1,162,205,52,18,163,205,52,10,164,205,52,1,165,205,52,1,166,205,52,1,167,205,52,1,168,205,52,1,169,205,52,1,170,205,52,1,171,205,52,1,172,205,52,1,173,205,52,1,174,205,52,1,175,205,53,1,176,205,53,1,177,205,53,1,178,205,53,1,179,205,49,1,180,205,46,1,181,205,42,1,182,205,39,1,183,205,35,1,184,205,32,1,185,210,28,1,186,215,25,1,187,221,21,1,188,226,18,1,189,232,14,1,190,237,11,1,191,243,7,1,192,248,4,9,193,254,1,18,194,254,1,26,195,254,1,35,196,254,1,43,197,254,1,52,198,254,1,60,199,254,1,68,200,254,1,76,201,254,1,85,202,254,1,93,203,254,1,102,204,254,1,110,205,254,1,119,206,254,1,128,207,254,1,132,208,254,1,137,209,254,1,142,210,254,1,147,211,254,1,152,212,254,1,156,213,254,1,161,214,254,1,166,215,254,1,171,216,254,1,176,217,254,1,180,218,254,1,185,219,254,1,190,220,254,1,194,221,254,1,199,222,254,1,204,223,254,1,204,224,254,1,204,225,254,1,204,226,254,15,204,227,254,29,204,228,254,43,204,229,247,57,204,230,241,70,204,231,235,84,204,232,229,99,204,233,223,113,204,234,217,127,204,235,211,141,204,236,205,155,204,237,205,169,204,238,205,183,204,239,205,197,204,240,205,200,204,241,205,203,204,242,205,206,204,243,205,209,204,244,205,212,204,245,205,215,204,246,205,218,207,247,205,221,210,248,205,224,213,249,209,227,216,250,213,230,219,251,217,233,222,252,221,236,225,253,225,239,228,254,229,242,231,255,229,242,231];
      return colorMap;
    }

    /**
     * Generates Prism colormap.
     * @return {Object} colorMap RGBA data array
     */
    getToftsVPColorMap() {
      // Prism
      const colorMap = [0,1,1,20,1,1,1,20,2,1,2,39,3,1,3,57,4,1,5,74,5,1,8,90,6,1,11,106,7,1,14,121,8,1,17,135,9,1,22,148,10,1,26,161,11,1,30,173,12,1,35,184,13,1,40,193,14,1,45,202,15,1,51,211,16,1,56,219,17,1,61,226,18,1,66,232,19,1,71,238,20,1,76,243,21,1,81,247,22,1,86,250,23,1,91,252,24,1,96,254,25,1,100,254,26,1,104,254,27,1,108,254,28,1,112,254,29,1,116,254,30,1,120,254,31,1,124,254,32,1,128,254,33,1,132,254,34,1,136,254,35,1,140,254,36,1,144,254,37,1,148,254,38,1,152,254,39,1,156,254,40,1,160,254,41,1,164,254,42,1,168,254,43,1,172,254,44,1,176,254,45,1,180,254,46,1,184,254,47,1,188,254,48,1,191,254,49,1,195,254,50,1,199,254,51,1,203,254,52,1,207,254,53,1,211,254,54,1,215,254,55,1,219,254,56,1,223,254,57,1,227,254,58,1,231,254,59,1,235,254,60,1,239,254,61,1,243,254,62,1,247,254,63,1,251,254,64,1,254,253,65,1,254,249,66,1,254,245,67,1,254,241,68,1,254,237,69,1,254,233,70,1,254,229,71,1,254,225,72,1,254,221,73,1,254,217,74,1,254,213,75,1,254,209,76,1,254,205,77,1,254,201,78,1,254,197,79,1,254,193,80,1,254,190,81,1,254,186,82,1,254,182,83,1,254,178,84,1,254,174,85,1,254,170,86,1,254,166,87,1,254,162,88,1,254,158,89,1,254,154,90,1,254,150,91,1,254,146,92,1,254,142,93,1,254,138,94,1,254,134,95,1,254,130,96,1,254,126,97,1,254,122,98,1,254,118,99,1,254,114,100,1,254,110,101,1,254,106,102,1,254,102,103,1,254,98,104,1,254,94,105,1,254,90,106,1,254,86,107,1,254,82,108,1,254,78,109,1,254,74,110,1,254,70,111,1,254,66,112,1,254,63,113,1,254,59,114,1,254,55,115,1,254,51,116,1,254,47,117,1,254,43,118,1,254,39,119,1,254,35,120,1,254,31,121,1,254,27,122,1,254,23,123,1,254,19,124,1,254,15,125,1,254,11,126,1,254,7,127,1,254,3,128,3,254,1,129,7,254,1,130,11,254,1,131,15,254,1,132,19,254,1,133,23,254,1,134,27,254,1,135,31,254,1,136,35,254,1,137,39,254,1,138,43,254,1,139,47,254,1,140,51,254,1,141,55,254,1,142,59,254,1,143,63,254,1,144,66,254,1,145,70,254,1,146,74,254,1,147,78,254,1,148,82,254,1,149,86,254,1,150,90,254,1,151,94,254,1,152,98,254,1,153,102,254,1,154,106,254,1,155,110,254,1,156,114,254,1,157,118,254,1,158,122,254,1,159,126,254,1,160,130,254,1,161,134,254,1,162,138,254,1,163,142,254,1,164,146,254,1,165,150,254,1,166,154,254,1,167,158,254,1,168,162,254,1,169,166,254,1,170,170,254,1,171,174,254,1,172,178,254,1,173,182,254,1,174,186,254,1,175,190,254,1,176,193,254,1,177,197,254,1,178,201,254,1,179,205,254,1,180,209,254,1,181,213,254,1,182,217,254,1,183,221,254,1,184,225,254,1,185,229,254,1,186,233,254,1,187,237,254,1,188,241,254,1,189,245,254,1,190,249,254,1,191,253,254,1,192,254,251,1,193,254,247,1,194,254,243,1,195,254,239,1,196,254,235,1,197,254,231,1,198,254,227,1,199,254,223,1,200,254,219,1,201,254,215,1,202,254,211,1,203,254,207,1,204,254,203,1,205,254,199,1,206,254,195,1,207,254,191,1,208,254,188,1,209,254,184,1,210,254,180,1,211,254,176,1,212,254,172,1,213,254,168,1,214,254,164,1,215,254,160,1,216,254,156,1,217,254,152,1,218,254,148,1,219,254,144,1,220,254,140,1,221,254,136,1,222,254,132,1,223,254,128,1,224,254,124,1,225,254,120,1,226,254,116,1,227,254,112,1,228,254,108,1,229,254,104,1,230,254,100,1,231,254,96,1,232,254,93,3,233,254,91,5,234,254,89,8,235,254,88,12,236,254,87,17,237,254,88,23,238,254,89,29,239,254,90,36,240,254,93,44,241,254,96,53,242,254,101,62,243,254,106,71,244,254,112,82,245,254,119,94,246,254,128,107,247,254,137,120,248,254,148,134,249,254,159,149,250,254,172,165,251,254,186,181,252,254,200,198,253,254,217,216,254,254,235,235,255,254,235,235];
      // A_Squared
      // const colorMap = [0,1,1,5,1,1,1,5,2,1,1,9,3,1,1,13,4,1,1,17,5,1,1,21,6,1,1,25,7,1,1,29,8,2,1,33,9,2,1,37,10,2,1,41,11,2,2,45,12,2,2,49,13,2,2,53,14,2,2,57,15,2,2,61,16,3,2,64,17,3,2,68,18,3,2,72,19,3,2,76,20,3,2,80,21,3,3,85,22,3,3,89,23,3,3,93,24,4,3,97,25,4,3,101,26,4,3,105,27,4,3,109,28,4,3,113,29,4,3,117,30,4,3,121,31,4,3,125,32,5,4,129,33,5,4,133,34,5,4,137,35,5,4,141,36,5,4,145,37,5,4,149,38,5,4,153,39,5,4,157,40,6,4,161,41,6,4,165,42,6,5,170,43,6,5,174,44,6,5,178,45,6,5,182,46,6,5,186,47,6,5,190,48,7,5,193,49,7,5,197,50,7,5,201,51,7,5,205,52,7,5,209,53,7,6,213,54,7,6,217,55,7,6,221,56,8,6,225,57,8,6,229,58,8,6,233,59,8,6,237,60,8,6,241,61,8,6,245,62,8,6,249,63,9,7,254,64,8,10,249,65,8,14,245,66,8,18,241,67,8,22,237,68,8,26,232,69,8,30,228,70,8,34,224,71,8,38,220,72,7,42,215,73,7,46,211,74,7,50,207,75,7,54,203,76,7,58,198,77,7,62,194,78,7,64,191,79,7,68,187,80,6,72,182,81,6,76,178,82,6,80,174,83,6,84,170,84,6,88,165,85,6,92,161,86,6,96,157,87,6,100,153,88,5,104,148,89,5,108,144,90,5,112,140,91,5,116,136,92,5,119,131,93,5,123,127,94,5,127,123,95,5,131,119,96,4,135,114,97,4,139,110,98,4,143,106,99,4,147,102,100,4,151,97,101,4,155,93,102,4,159,89,103,4,163,85,104,3,167,80,105,3,171,76,106,3,174,72,107,3,178,68,108,3,182,64,109,3,186,60,110,3,190,56,111,3,193,52,112,2,197,47,113,2,201,43,114,2,205,39,115,2,209,35,116,2,213,30,117,2,217,26,118,2,221,22,119,2,225,18,120,8,225,2,121,12,226,2,122,16,226,2,123,19,227,2,124,23,227,2,125,27,228,2,126,31,228,2,127,34,229,2,128,38,229,2,129,42,229,2,130,46,230,2,131,49,230,2,132,53,231,2,133,57,231,2,134,61,232,2,135,64,232,2,136,67,233,2,137,71,233,2,138,75,234,2,139,79,234,2,140,82,234,2,141,86,235,2,142,90,235,2,143,94,236,2,144,97,236,2,145,101,237,2,146,105,237,2,147,109,238,2,148,112,238,2,149,116,239,2,150,120,239,2,151,124,239,1,152,128,240,1,153,131,240,1,154,135,241,1,155,139,241,1,156,143,242,1,157,146,242,1,158,150,243,1,159,154,243,1,160,158,244,1,161,161,244,1,162,165,244,1,163,169,245,1,164,173,245,1,165,176,246,1,166,180,246,1,167,184,247,1,168,188,247,1,169,191,248,1,170,194,248,1,171,198,249,1,172,202,249,1,173,206,249,1,174,209,250,1,175,213,250,1,176,217,251,1,177,221,251,1,178,224,252,1,179,228,252,1,180,232,253,1,181,236,253,1,182,240,254,1,183,240,249,1,184,240,245,1,185,240,240,1,186,240,236,1,187,241,232,2,188,241,227,2,189,241,223,2,190,241,218,2,191,242,214,2,192,242,210,3,193,242,205,3,194,242,201,3,195,243,196,3,196,243,192,3,197,243,189,4,198,243,184,4,199,244,180,4,200,244,176,4,201,244,171,5,202,244,167,5,203,245,162,5,204,245,158,5,205,245,154,5,206,245,149,6,207,246,145,6,208,246,140,6,209,246,136,6,210,246,132,6,211,247,127,7,212,247,123,7,213,247,119,7,214,247,114,7,215,248,110,7,216,248,105,8,217,248,101,8,218,248,97,8,219,249,92,8,220,249,88,9,221,249,83,9,222,249,79,9,223,250,75,9,224,250,70,9,225,250,66,10,226,250,63,10,227,251,58,10,228,251,54,10,229,251,49,10,230,251,45,11,231,252,41,11,232,252,36,11,233,252,32,11,234,252,27,11,235,253,23,12,236,253,19,12,237,253,14,12,238,253,10,12,239,254,6,13,240,246,74,19,241,247,88,37,242,247,102,55,243,248,116,72,244,248,129,90,245,249,143,108,246,249,157,127,247,250,171,145,248,250,185,163,249,251,197,181,250,251,211,198,251,252,225,216,252,252,239,234,253,253,246,242,254,253,253,253,255,253,253,253];
      return colorMap;
    }

    /**
     * Generates Gold colormap.
     * @return {Object} colorMap RGBA data array
     */
    getToftsKtransColorMap() {
      // Blue
      // const colorMap = [0,1,1,10,1,1,1,10,2,1,1,20,3,1,1,30,4,1,2,39,5,1,2,48,6,1,3,56,7,1,4,64,8,1,5,71,9,1,6,79,10,1,7,87,11,1,8,94,12,1,9,100,13,1,10,107,14,1,12,113,15,1,13,118,16,1,14,124,17,1,16,128,18,1,17,133,19,1,18,137,20,1,20,141,21,1,21,144,22,1,22,147,23,1,23,149,24,1,25,151,25,1,26,153,26,1,27,154,27,1,28,155,28,1,29,156,29,1,30,157,30,1,31,158,31,1,32,159,32,1,33,160,33,1,34,161,34,1,35,162,35,1,36,163,36,1,37,164,37,1,38,165,38,1,39,166,39,1,40,167,40,1,41,168,41,1,42,169,42,1,43,170,43,1,44,171,44,1,45,172,45,1,46,173,46,1,47,174,47,1,48,175,48,1,49,176,49,1,50,177,50,1,51,178,51,1,52,179,52,1,53,180,53,1,54,181,54,1,55,182,55,1,56,183,56,1,57,184,57,1,58,185,58,1,59,186,59,1,60,187,60,1,61,188,61,1,62,189,62,1,63,190,63,1,64,191,64,1,64,191,65,1,65,192,66,1,66,193,67,1,67,194,68,1,68,195,69,1,69,196,70,1,70,197,71,1,71,198,72,1,72,199,73,1,73,200,74,1,74,201,75,1,75,202,76,1,76,203,77,1,77,204,78,1,78,205,79,1,79,206,80,1,80,207,81,1,81,208,82,1,82,209,83,1,83,210,84,1,84,211,85,1,85,212,86,1,86,213,87,1,87,214,88,1,88,215,89,1,89,216,90,1,90,217,91,1,91,218,92,1,92,219,93,1,93,220,94,1,94,221,95,1,95,222,96,1,96,223,97,1,97,224,98,1,98,225,99,1,99,226,100,1,100,227,101,1,101,228,102,1,102,229,103,1,103,230,104,1,104,231,105,1,105,232,106,1,106,233,107,1,107,234,108,1,108,235,109,1,109,236,110,1,110,237,111,1,111,238,112,1,112,239,113,1,113,240,114,1,114,241,115,1,115,242,116,1,116,243,117,1,117,244,118,1,118,245,119,1,119,246,120,1,120,247,121,1,121,248,122,1,122,249,123,1,123,250,124,1,124,251,125,1,125,252,126,1,126,253,127,1,127,254,128,1,128,254,129,1,129,254,130,1,130,254,131,1,131,254,132,1,132,254,133,1,133,254,134,1,134,254,135,1,135,254,136,1,136,254,137,1,137,254,138,1,138,254,139,1,139,254,140,1,140,254,141,1,141,254,142,1,142,254,143,1,143,254,144,1,144,254,145,1,145,254,146,1,146,254,147,1,147,254,148,1,148,254,149,1,149,254,150,1,150,254,151,1,151,254,152,1,152,254,153,1,153,254,154,1,154,254,155,1,155,254,156,1,156,254,157,1,157,254,158,1,158,254,159,1,159,254,160,1,160,254,161,1,161,254,162,1,162,254,163,1,163,254,164,1,164,254,165,1,165,254,166,1,166,254,167,1,167,254,168,1,168,254,169,1,169,254,170,1,170,254,171,1,171,254,172,1,172,254,173,1,173,254,174,1,174,254,175,1,175,254,176,1,176,254,177,1,177,254,178,1,178,254,179,1,179,254,180,1,180,254,181,1,181,254,182,1,182,254,183,1,183,254,184,1,184,254,185,1,185,254,186,1,186,254,187,1,187,254,188,1,188,254,189,1,189,254,190,1,190,254,191,1,191,254,192,1,191,254,193,1,192,254,194,1,193,254,195,1,194,254,196,1,195,254,197,1,196,254,198,1,197,254,199,1,198,254,200,1,199,254,201,1,200,254,202,1,201,254,203,1,202,254,204,1,203,254,205,1,204,254,206,1,205,254,207,1,206,254,208,1,207,254,209,1,208,254,210,1,209,254,211,1,210,254,212,1,211,254,213,1,212,254,214,1,213,254,215,1,214,254,216,1,215,254,217,1,216,254,218,1,217,254,219,1,218,254,220,1,219,254,221,1,220,254,222,1,221,254,223,1,222,254,224,1,223,254,225,1,224,254,226,1,225,254,227,1,226,254,228,1,227,254,229,1,228,254,230,1,229,254,231,1,230,254,232,3,232,254,233,5,233,254,234,8,234,254,235,12,235,254,236,17,237,254,237,23,238,254,238,29,239,254,239,36,241,254,240,44,242,254,241,53,243,254,242,62,245,254,243,71,246,254,244,82,247,254,245,94,248,254,246,107,249,254,247,120,250,254,248,134,251,254,249,149,252,254,250,165,253,254,251,181,253,254,252,198,254,254,253,216,254,254,254,235,254,254,255,235,254,254];
      // CMY
      // const colorMap = [0,0,0,0,1,0,255,255,2,0,255,255,3,0,255,255,4,0,255,255,5,0,255,255,6,0,255,255,7,0,255,255,8,0,255,255,9,0,255,255,10,0,255,255,11,0,255,255,12,0,255,255,13,0,255,255,14,0,255,255,15,0,255,255,16,0,255,255,17,0,255,255,18,0,255,255,19,0,255,255,20,0,255,255,21,0,255,255,22,0,255,255,23,0,255,255,24,0,255,255,25,0,255,255,26,0,255,255,27,0,255,255,28,0,255,255,29,0,255,255,30,0,255,255,31,0,255,255,32,0,255,255,33,0,255,255,34,0,255,255,35,0,255,255,36,0,255,255,37,0,255,255,38,0,255,255,39,0,255,255,40,0,255,255,41,0,255,255,42,0,255,255,43,0,255,255,44,0,255,255,45,0,255,255,46,0,255,255,47,0,255,255,48,0,255,255,49,0,255,255,50,0,255,255,51,0,255,255,52,0,255,255,53,0,255,255,54,0,255,255,55,0,255,255,56,0,255,255,57,0,255,255,58,0,255,255,59,0,255,255,60,0,255,255,61,0,255,255,62,0,255,255,63,0,255,255,64,0,255,255,65,0,255,255,66,0,255,255,67,0,255,255,68,0,255,255,69,0,255,255,70,0,255,255,71,0,255,255,72,0,255,255,73,0,255,255,74,0,255,255,75,0,255,255,76,0,255,255,77,0,255,255,78,0,255,255,79,0,255,255,80,0,255,255,81,0,255,255,82,0,255,255,83,0,255,255,84,0,255,255,85,255,0,255,86,255,0,255,87,255,0,255,88,255,0,255,89,255,0,255,90,255,0,255,91,255,0,255,92,255,0,255,93,255,0,255,94,255,0,255,95,255,0,255,96,255,0,255,97,255,0,255,98,255,0,255,99,255,0,255,100,255,0,255,101,255,0,255,102,255,0,255,103,255,0,255,104,255,0,255,105,255,0,255,106,255,0,255,107,255,0,255,108,255,0,255,109,255,0,255,110,255,0,255,111,255,0,255,112,255,0,255,113,255,0,255,114,255,0,255,115,255,0,255,116,255,0,255,117,255,0,255,118,255,0,255,119,255,0,255,120,255,0,255,121,255,0,255,122,255,0,255,123,255,0,255,124,255,0,255,125,255,0,255,126,255,0,255,127,255,0,255,128,255,0,255,129,255,0,255,130,255,0,255,131,255,0,255,132,255,0,255,133,255,0,255,134,255,0,255,135,255,0,255,136,255,0,255,137,255,0,255,138,255,0,255,139,255,0,255,140,255,0,255,141,255,0,255,142,255,0,255,143,255,0,255,144,255,0,255,145,255,0,255,146,255,0,255,147,255,0,255,148,255,0,255,149,255,0,255,150,255,0,255,151,255,0,255,152,255,0,255,153,255,0,255,154,255,0,255,155,255,0,255,156,255,0,255,157,255,0,255,158,255,0,255,159,255,0,255,160,255,0,255,161,255,0,255,162,255,0,255,163,255,0,255,164,255,0,255,165,255,0,255,166,255,0,255,167,255,0,255,168,255,0,255,169,255,255,0,170,255,255,0,171,255,255,0,172,255,255,0,173,255,255,0,174,255,255,0,175,255,255,0,176,255,255,0,177,255,255,0,178,255,255,0,179,255,255,0,180,255,255,0,181,255,255,0,182,255,255,0,183,255,255,0,184,255,255,0,185,255,255,0,186,255,255,0,187,255,255,0,188,255,255,0,189,255,255,0,190,255,255,0,191,255,255,0,192,255,255,0,193,255,255,0,194,255,255,0,195,255,255,0,196,255,255,0,197,255,255,0,198,255,255,0,199,255,255,0,200,255,255,0,201,255,255,0,202,255,255,0,203,255,255,0,204,255,255,0,205,255,255,0,206,255,255,0,207,255,255,0,208,255,255,0,209,255,255,0,210,255,255,0,211,255,255,0,212,255,255,0,213,255,255,0,214,255,255,0,215,255,255,0,216,255,255,0,217,255,255,0,218,255,255,0,219,255,255,0,220,255,255,0,221,255,255,0,222,255,255,0,223,255,255,0,224,255,255,0,225,255,255,0,226,255,255,0,227,255,255,0,228,255,255,0,229,255,255,0,230,255,255,0,231,255,255,0,232,255,255,0,233,255,255,0,234,255,255,0,235,255,255,0,236,255,255,0,237,255,255,0,238,255,255,0,239,255,255,0,240,255,255,0,241,255,255,0,242,255,255,0,243,255,255,0,244,255,255,0,245,255,255,0,246,255,255,0,247,255,255,0,248,255,255,0,249,255,255,0,250,255,255,0,251,255,255,0,252,255,255,0,253,240,240,240,254,248,248,248,255,255,255,255];
      //Gold
      const colorMap = [0,231,243,155,1,231,243,155,2,242,254,154,3,242,254,153,4,243,254,152,5,243,254,150,6,243,254,149,7,243,254,148,8,244,254,147,9,244,254,146,10,244,254,145,11,245,254,144,12,245,254,143,13,245,254,142,14,246,254,141,15,246,254,140,16,246,254,139,17,247,254,138,18,247,254,137,19,248,254,136,20,248,254,135,21,249,254,133,22,249,254,132,23,250,254,131,24,250,254,130,25,251,254,129,26,251,254,128,27,252,254,127,28,252,254,126,29,253,254,125,30,253,254,124,31,254,254,123,32,254,253,122,33,254,253,121,34,254,252,120,35,254,251,119,36,254,251,118,37,254,250,116,38,254,249,115,39,254,248,114,40,254,248,113,41,254,247,112,42,254,246,111,43,254,245,110,44,254,245,109,45,254,244,108,46,254,243,107,47,254,242,106,48,254,241,105,49,254,240,104,50,254,239,103,51,254,238,102,52,254,237,101,53,253,237,99,54,253,236,98,55,253,235,97,56,253,234,96,57,253,233,95,58,253,232,94,59,253,231,93,60,253,230,92,61,253,228,91,62,253,227,90,63,253,226,89,64,253,225,88,65,253,224,87,66,253,223,86,67,253,222,85,68,253,221,84,69,253,219,82,70,253,218,81,71,253,217,80,72,253,216,79,73,253,215,78,74,253,213,77,75,253,212,76,76,253,211,75,77,253,210,74,78,253,208,73,79,253,207,72,80,253,206,71,81,253,204,70,82,253,203,69,83,253,202,68,84,253,200,67,85,253,199,65,86,253,197,64,87,253,196,63,88,253,195,62,89,253,193,61,90,253,192,60,91,253,190,59,92,253,189,58,93,253,187,57,94,253,186,56,95,253,184,55,96,253,183,54,97,253,181,53,98,253,179,52,99,251,177,51,100,250,175,50,101,248,173,48,102,246,171,47,103,245,168,46,104,243,166,45,105,242,164,44,106,240,162,43,107,238,160,42,108,237,158,41,109,235,156,40,110,233,153,39,111,232,151,38,112,230,149,37,113,229,147,36,114,227,145,35,115,225,143,34,116,224,141,33,117,222,139,31,118,221,137,30,119,219,135,29,120,217,133,28,121,216,131,27,122,214,129,26,123,212,128,25,124,211,126,24,125,209,124,23,126,208,122,22,127,206,120,21,128,204,118,20,129,203,116,19,130,201,115,18,131,199,113,17,132,198,111,16,133,196,109,14,134,195,107,13,135,193,106,12,136,191,104,11,137,190,102,10,138,188,101,9,139,186,99,8,140,185,97,7,141,183,96,6,142,182,94,5,143,180,92,4,144,178,91,3,145,177,89,2,146,175,87,1,147,173,86,0,148,172,84,1,149,170,83,1,150,169,81,1,151,167,80,1,152,165,78,1,153,164,77,1,154,162,75,1,155,161,74,1,156,159,72,1,157,157,71,1,158,156,69,1,159,154,68,1,160,152,67,1,161,151,65,1,162,149,64,1,163,148,63,1,164,146,61,1,165,144,60,1,166,143,59,1,167,141,57,1,168,139,56,1,169,138,55,1,170,136,54,1,171,135,52,1,172,133,51,1,173,131,50,1,174,130,49,1,175,128,48,1,176,126,47,1,177,125,45,1,178,123,44,1,179,122,43,1,180,120,42,1,181,118,41,1,182,117,40,1,183,115,39,1,184,113,38,1,185,112,37,1,186,110,36,1,187,109,35,1,188,107,34,0,189,105,33,0,190,104,32,0,191,102,31,0,192,100,30,0,193,99,29,0,194,97,28,0,195,96,27,0,196,94,26,0,197,92,25,0,198,91,24,0,199,89,24,0,200,87,23,0,201,86,22,0,202,84,21,0,203,83,20,0,204,81,20,0,205,79,19,0,206,78,18,0,207,76,17,0,208,74,17,0,209,73,16,0,210,71,15,0,211,70,15,0,212,68,14,0,213,66,13,0,214,65,13,0,215,63,12,0,216,61,12,0,217,60,11,0,218,58,10,0,219,57,10,0,220,55,9,0,221,53,9,0,222,52,8,0,223,50,8,0,224,48,7,0,225,47,7,0,226,45,6,0,227,44,6,0,228,42,6,0,229,40,5,0,230,39,5,0,231,37,4,0,232,35,4,0,233,34,4,0,234,32,3,0,235,31,3,0,236,29,3,0,237,27,2,0,238,26,2,0,239,24,2,0,240,22,2,0,241,21,1,0,242,19,1,0,243,18,1,0,244,16,1,0,245,14,1,0,246,13,0,0,247,11,0,0,248,9,0,0,249,8,0,0,250,6,0,0,251,5,0,0,252,3,0,0,253,1,0,0,254,0,0,0,255,0,0,0];
      return colorMap;
    }

    /**
     * Generates CMY colormap.
     * @return {Object} colorMap RGBA data array
     */
    getFTBColorMap() {
      //CMY
      const colorMap = [0,0,0,0,1,0,255,255,2,0,255,255,3,0,255,255,4,0,255,255,5,0,255,255,6,0,255,255,7,0,255,255,8,0,255,255,9,0,255,255,10,0,255,255,11,0,255,255,12,0,255,255,13,0,255,255,14,0,255,255,15,0,255,255,16,0,255,255,17,0,255,255,18,0,255,255,19,0,255,255,20,0,255,255,21,0,255,255,22,0,255,255,23,0,255,255,24,0,255,255,25,0,255,255,26,0,255,255,27,0,255,255,28,0,255,255,29,0,255,255,30,0,255,255,31,0,255,255,32,0,255,255,33,0,255,255,34,0,255,255,35,0,255,255,36,0,255,255,37,0,255,255,38,0,255,255,39,0,255,255,40,0,255,255,41,0,255,255,42,0,255,255,43,0,255,255,44,0,255,255,45,0,255,255,46,0,255,255,47,0,255,255,48,0,255,255,49,0,255,255,50,0,255,255,51,0,255,255,52,0,255,255,53,0,255,255,54,0,255,255,55,0,255,255,56,0,255,255,57,0,255,255,58,0,255,255,59,0,255,255,60,0,255,255,61,0,255,255,62,0,255,255,63,0,255,255,64,0,255,255,65,0,255,255,66,0,255,255,67,0,255,255,68,0,255,255,69,0,255,255,70,0,255,255,71,0,255,255,72,0,255,255,73,0,255,255,74,0,255,255,75,0,255,255,76,0,255,255,77,0,255,255,78,0,255,255,79,0,255,255,80,0,255,255,81,0,255,255,82,0,255,255,83,0,255,255,84,0,255,255,85,255,0,255,86,255,0,255,87,255,0,255,88,255,0,255,89,255,0,255,90,255,0,255,91,255,0,255,92,255,0,255,93,255,0,255,94,255,0,255,95,255,0,255,96,255,0,255,97,255,0,255,98,255,0,255,99,255,0,255,100,255,0,255,101,255,0,255,102,255,0,255,103,255,0,255,104,255,0,255,105,255,0,255,106,255,0,255,107,255,0,255,108,255,0,255,109,255,0,255,110,255,0,255,111,255,0,255,112,255,0,255,113,255,0,255,114,255,0,255,115,255,0,255,116,255,0,255,117,255,0,255,118,255,0,255,119,255,0,255,120,255,0,255,121,255,0,255,122,255,0,255,123,255,0,255,124,255,0,255,125,255,0,255,126,255,0,255,127,255,0,255,128,255,0,255,129,255,0,255,130,255,0,255,131,255,0,255,132,255,0,255,133,255,0,255,134,255,0,255,135,255,0,255,136,255,0,255,137,255,0,255,138,255,0,255,139,255,0,255,140,255,0,255,141,255,0,255,142,255,0,255,143,255,0,255,144,255,0,255,145,255,0,255,146,255,0,255,147,255,0,255,148,255,0,255,149,255,0,255,150,255,0,255,151,255,0,255,152,255,0,255,153,255,0,255,154,255,0,255,155,255,0,255,156,255,0,255,157,255,0,255,158,255,0,255,159,255,0,255,160,255,0,255,161,255,0,255,162,255,0,255,163,255,0,255,164,255,0,255,165,255,0,255,166,255,0,255,167,255,0,255,168,255,0,255,169,255,255,0,170,255,255,0,171,255,255,0,172,255,255,0,173,255,255,0,174,255,255,0,175,255,255,0,176,255,255,0,177,255,255,0,178,255,255,0,179,255,255,0,180,255,255,0,181,255,255,0,182,255,255,0,183,255,255,0,184,255,255,0,185,255,255,0,186,255,255,0,187,255,255,0,188,255,255,0,189,255,255,0,190,255,255,0,191,255,255,0,192,255,255,0,193,255,255,0,194,255,255,0,195,255,255,0,196,255,255,0,197,255,255,0,198,255,255,0,199,255,255,0,200,255,255,0,201,255,255,0,202,255,255,0,203,255,255,0,204,255,255,0,205,255,255,0,206,255,255,0,207,255,255,0,208,255,255,0,209,255,255,0,210,255,255,0,211,255,255,0,212,255,255,0,213,255,255,0,214,255,255,0,215,255,255,0,216,255,255,0,217,255,255,0,218,255,255,0,219,255,255,0,220,255,255,0,221,255,255,0,222,255,255,0,223,255,255,0,224,255,255,0,225,255,255,0,226,255,255,0,227,255,255,0,228,255,255,0,229,255,255,0,230,255,255,0,231,255,255,0,232,255,255,0,233,255,255,0,234,255,255,0,235,255,255,0,236,255,255,0,237,255,255,0,238,255,255,0,239,255,255,0,240,255,255,0,241,255,255,0,242,255,255,0,243,255,255,0,244,255,255,0,245,255,255,0,246,255,255,0,247,255,255,0,248,255,255,0,249,255,255,0,250,255,255,0,251,255,255,0,252,255,255,0,253,240,240,240,254,248,248,248,255,255,255,255];
      return colorMap;
    }

    /**
     * Generates Colorful colormap.
     * @return {Object} colorMap RGBA data array
     */
    getK2ColorMap() {
      //Colorful
      const colorMap = [0,4,1,4,1,4,1,4,2,7,1,8,3,9,1,13,4,10,1,17,5,13,1,21,6,16,1,25,7,18,1,30,8,19,1,34,9,22,1,38,10,25,1,42,11,27,1,47,12,28,1,51,13,31,1,55,14,34,1,59,15,35,1,64,16,38,1,67,17,41,1,71,18,43,1,75,19,44,1,80,20,47,1,84,21,50,1,88,22,52,1,92,23,53,1,97,24,56,1,101,25,59,1,105,26,61,1,109,27,62,1,114,28,64,1,118,29,67,1,122,30,68,1,127,31,68,1,127,32,67,1,131,33,64,1,135,34,62,1,139,35,61,1,143,36,59,1,147,37,56,1,151,38,53,1,155,39,52,1,159,40,50,1,163,41,47,1,167,42,44,1,171,43,43,1,175,44,41,1,179,45,38,1,183,46,35,1,187,47,34,1,191,48,31,1,194,49,28,1,198,50,27,1,202,51,25,1,206,52,22,1,210,53,19,1,214,54,18,1,218,55,16,1,222,56,13,1,226,57,10,1,230,58,9,1,234,59,7,1,238,60,4,1,242,61,2,1,246,62,1,1,254,63,1,1,254,64,1,5,246,65,1,10,242,66,1,14,238,67,1,19,234,68,1,23,230,69,1,28,226,70,1,32,222,71,1,37,218,72,1,41,214,73,1,46,210,74,1,50,206,75,1,55,202,76,1,59,198,77,1,64,194,78,1,67,191,79,1,72,187,80,1,76,183,81,1,81,179,82,1,85,175,83,1,90,171,84,1,94,167,85,1,99,163,86,1,103,159,87,1,108,155,88,1,112,151,89,1,117,147,90,1,121,143,91,1,126,139,92,1,130,135,93,1,135,131,94,1,139,127,95,1,144,122,96,1,148,118,97,1,153,114,98,1,157,109,99,1,162,105,100,1,166,101,101,1,171,97,102,1,175,92,103,1,180,88,104,1,184,84,105,1,189,80,106,1,192,75,107,1,203,71,108,1,203,67,109,10,205,64,110,19,207,59,111,28,209,55,112,37,211,51,113,46,213,47,114,55,215,42,115,64,217,38,116,72,219,34,117,81,221,30,118,90,223,25,119,99,225,21,120,108,227,17,121,117,229,13,122,126,231,11,123,135,233,8,124,144,235,5,125,153,237,2,126,162,239,1,127,171,241,1,128,180,242,1,129,189,244,1,130,197,245,1,131,206,247,1,132,215,248,1,133,224,250,1,134,233,251,1,135,242,253,1,136,254,254,1,137,254,254,1,138,254,253,1,139,254,251,1,140,254,249,1,141,254,247,1,142,254,245,1,143,254,243,1,144,254,241,1,145,254,239,1,146,254,237,1,147,254,235,1,148,254,233,1,149,254,231,1,150,254,229,1,151,254,227,1,152,254,225,1,153,254,223,1,154,254,221,1,155,254,219,1,156,254,217,1,157,254,215,1,158,254,213,1,159,254,211,1,160,254,209,1,161,254,207,1,162,254,205,1,163,254,203,1,164,254,201,1,165,254,199,1,166,254,197,1,167,254,195,1,168,254,193,1,169,254,191,1,170,254,190,1,171,254,188,1,172,254,186,1,173,254,184,1,174,254,182,1,175,254,180,1,176,254,178,1,177,254,176,1,178,254,174,1,179,254,172,1,180,254,170,1,181,254,168,1,182,254,166,1,183,254,164,1,184,254,162,1,185,254,160,1,186,254,158,1,187,254,156,1,188,254,154,1,189,254,152,1,190,254,150,1,191,254,148,1,192,254,146,1,193,254,144,1,194,254,142,1,195,254,140,1,196,254,138,1,197,254,136,1,198,254,134,1,199,254,132,1,200,254,130,1,201,254,128,1,202,254,126,1,203,254,124,1,204,254,122,1,205,254,120,1,206,254,118,1,207,254,116,1,208,254,114,1,209,254,112,1,210,254,110,1,211,254,108,1,212,254,106,1,213,254,104,1,214,254,102,1,215,254,100,1,216,254,98,1,217,254,96,1,218,254,94,1,219,254,92,1,220,254,90,1,221,254,88,1,222,254,86,1,223,254,84,1,224,254,82,1,225,254,80,1,226,254,78,1,227,254,76,1,228,254,74,1,229,254,72,1,230,254,69,1,231,254,66,1,232,254,64,1,233,254,62,1,234,254,59,1,235,254,56,1,236,254,54,1,237,254,51,1,238,254,48,1,239,254,45,1,240,254,43,1,241,254,40,1,242,254,37,1,243,254,34,1,244,254,32,1,245,254,29,1,246,254,26,1,247,254,23,1,248,254,21,1,249,254,18,1,250,254,15,1,251,254,12,1,252,254,10,1,253,254,7,1,254,254,4,1,255,254,4,1];
      return colorMap;
    }

    /**
     * Generates Red Blue colormap.
     * @return {Object} colorMap RGBA data array
     */
    getRedBlueColorMap() {
        const colorMap = [];
        const maxValue = 255;
        for (let i = 0; i <= maxValue; i++) {
            colorMap[i] = {
                r: (maxValue-i),
                g: (128-(Math.abs(128 - i))),
                b: i,
                a: maxValue
            };
        }
        colorMap[0].a = 0; // make zero values invisible
        return colorMap;
    }

    /**
     * Generates Red Green Blue colormap.
     * @return {Object} colorMap RGBA data array
     */
    getRGBColorMap() {
        const colorMap = [];
        const maxValue = 255;
        for (let i = 0; i <= maxValue; i++) {
            colorMap[i] = {
                r: 0,
                g: 0,
                b: 0,
                a: maxValue
            }
        }
        colorMap[1].b = 255;
        colorMap[2].g = 255;
        colorMap[3].r = 255;
        colorMap[4].b = 255;
        colorMap[4].r = 255;
        colorMap[4].g = 255;
        colorMap[0].a = 0; //make zero values invisible
        return colorMap;
    }

    /**
     * Generates custom gradient colormap between two colours.
     * @param {string} fromHex Hexidecimal value of first colour
     * @param {string} toHex Hexidecimal value of second colour
     * @return {Object} colormap RGBA data array
     */
    getCustomGradient(fromHex, toHex) {
        // Get custom
        const gradient = [];
        const maxValue = 255;
        const from = getRGB(fromHex);
        const to = getRGB(toHex);
        for (let i = 0; i <= maxValue; i++) {
            const intensityB = i;
            const intensityA = maxValue - intensityB;
            gradient[i] = {
                r: (intensityA*from.r + intensityB*to.r) / maxValue,
                g: (intensityA*from.g + intensityB*to.g) / maxValue,
                b: (intensityA*from.b + intensityB*to.b) / maxValue,
                a: 255
            }
        }
        gradient[0].a = 0;
        return gradient;

        /**
         * Converts hexidecimal to RGB.
         * @param {string} hex Hexidecimal value
         * @return {Object} rgb RGB values dict
         */
        function getRGB(hex) {
          let hexInt;
          if (hex[0] === '#') {
              hex = hex.substr(1);
          }
          hexInt = parseInt(hex, 16);
          const rgb = {
              r: hexInt >> 16,
              g: (hexInt >> 8) & 255,
              b: hexInt & 255
          };
          return rgb;
        }
    }

    get_latest_maps(seriesChildren) {
      let lastest_version = 1;
      const version_to_maps = {}

      seriesChildren.forEach(map_series => {
        const series_version = map_series['generatedSeriesVersion'] ? parseInt(map_series['generatedSeriesVersion']) : 1;
        lastest_version = series_version > lastest_version ? series_version : lastest_version;

        if(!version_to_maps['version-'+series_version]) {
          version_to_maps['version-'+series_version] = [map_series];
        }
        else {
          version_to_maps['version-'+series_version].push(map_series);
        }
      });
      // console.log('lastest_version:', lastest_version);
      // console.log('version_to_maps:', version_to_maps);

      return version_to_maps['version-'+lastest_version];
    }

    initializeParametricMapsSubMenu(cell: lt.Controls.Medical.Cell, audit?: ViewerAuditableContext) {
      // console.log('initializeParametricMapsSubMenu...');
      let seriesInfo = this.seriesManagerService.getSeriesInfo(cell);

      if(!this.cellOverlays[cell.divID]){
        this.initializeCellOverlay(cell);
      }

      // Initialize the parametric maps (PM) & motion correction (MC)
      return this.imagingProjectService.getChildSeries(seriesInfo.id[0]).toPromise().then(async (rsp: any) => {
        // console.log("initializeParametricMapsSubMenu, rsp: ", rsp);
        const seriesChildren = this.get_latest_maps(rsp.data) || [];
        this._parametricMaps[cell.divID] = {"isMC": false, "mcInfo": {}};
        let mc_info = null;
        const pm_infos = [];
        let pm_subMenuItems: any = [];

        // Parametric maps element
        // if (seriesInfo.isDCE){
          for (let i=0; i<seriesChildren.length; i++) {
            if(seriesChildren[i].mapName == "2D Motion Correction"){
              mc_info = seriesChildren[i];
              continue;
            }
            pm_infos.push(seriesChildren[i]);
          }
          for (let i=0; i<pm_infos.length; i++) {
            this._parametricMaps[cell.divID]["DCE-"+String(i)] = {[pm_infos[i].seriesUID]: "null"};
            pm_subMenuItems.push({
              "id": pm_infos[i].mapName,
              "action": "ApplyParametricMap",
              "caption": pm_infos[i].mapName,
              "tooltip": pm_infos[i].mapName,
              "type": "button",
              "info": {
                "mapName": pm_infos[i].mapName,
                "mapIdx": i, 
                "mapSeriesUID": pm_infos[i].seriesUID, 
                "rootSeriesId": (<any>cell).seriesId,
                "audit": audit,
              },
              "items": []
            });
          }
        // }
        const cellOverlay = this.cellOverlays[cell.divID];
        const cellPM = this._parametricMaps[cell.divID];

        if (cellOverlay) {
          cellOverlay.seriesChildren = pm_infos;
          cellOverlay.mcInfo = mc_info;
        }

        if (cellPM) {
          cellPM.mcInfo = mc_info;
          cellPM.pmInfos = pm_infos;
        }

        const mc_subMenuItems: any = [];
        if (mc_info != null) {
          mc_subMenuItems.push({
            id: mc_info.mapName,
            action: 'ApplyMotionCorrection',
            caption: mc_info.mapName,
            tooltip: mc_info.mapName,
            type: 'button',
            info: {
              mapName: mc_info.mapName,
              mapIdx: 0,
              seriesUID: mc_info.seriesUID
            },
            items: []
          });
        }

        // Sort PM subMenu
        pm_subMenuItems.sort((a, b) => (a.id > b.id) ? 1 : -1);

        // Remove duplicates from PM subMenu
        pm_subMenuItems = pm_subMenuItems.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i);

        pm_subMenuItems.insert(0, {
          "id": "NONE",
          "action": "ApplyParametricMap",
          "caption": "None",
          "tooltip": "None",
          "type": "button",
          "info": {
            "mapName": "None", 
            "mapIdx": "NONE", 
            "mapSeriesUID": null, 
            "rootSeriesId": (<any>cell).seriesId,
            "audit": audit
          },
          "items": []
        });

        if (mc_info == null) {
          if (!seriesInfo.parents || seriesInfo.parents.length == 0) {
            let res = await this.imagingProjectService.getSeriesInfo(seriesInfo.id[0]).toPromise();
            if (res && res.data && res.data.parents)
              seriesInfo.parents = res.data.parents;
          }

          if (seriesInfo.parents && seriesInfo.parents.length)
            mc_subMenuItems.insert(0, {
              "id": "NONE",
              "action": "ApplyMotionCorrection",
              "caption": "None",
              "tooltip": "None",
              "type": "button",
              "info": { "mapName": "None", "mapIdx": "NONE", "seriesId": seriesInfo.id[0] },
              "items": []
            });
        }
        return {'pm_subMenuItems': pm_subMenuItems, 'mc_subMenuItems': mc_subMenuItems};
      });
    }

    applyMotionCorrection(info){
      console.log("Action: applyMotionCorrection...");
      const cell: lt.Controls.Medical.Cell = this.seriesManagerService.getActiveCell();
      const mcInfo = this._parametricMaps[cell.divID]["mcInfo"];
      const viewerComponent = info.viewerComponent;
      let seriesId;

      if (mcInfo === null) {
        // Get the series_id of initial DCE
        seriesId = info.seriesId;
        console.log(`Loading Initial DCE with seriesId: ${seriesId}`);
        this._parametricMaps[cell.divID]["isMC"] = false;
      } else {
        // Get the series_id of Motion Corrected DCE
        seriesId = mcInfo.id;
        const mcName = mcInfo.mapName;
        console.log(`Loading Motion Corrected DCE with seriesId: ${seriesId} and MapName: ${mcName}`);
        this._parametricMaps[cell.divID]["isMC"] = true;
      }
      viewerComponent.removeCell(cell, undefined, undefined, false);

      if (viewerComponent.viewerData === null || viewerComponent.viewerData === undefined
          || !viewerComponent.viewerData[seriesId] || !viewerComponent.viewerData[seriesId]['timepoint']
          || !viewerComponent.viewerData['patient'] || !viewerComponent.viewerData['authenticationCode']) {

        viewerComponent.isViewerActionInProgress = true;
        viewerComponent.viewerProgressActionText = `Preparing Motion Corrected DCE...`;
        viewerComponent.imagingProjectService.getSeriesInstanceUID([parseInt(seriesId, 10)]).subscribe((rsp: any) => {
            viewerComponent.imagingProjectService.getVisitFromSeriesID(seriesId).toPromise().then((visit_rsp: any) => {
                const timepoint = visit_rsp.data.visitName ? visit_rsp.data.visitName : 'Missing';
                const seriesList = rsp.data;

                viewerComponent.isViewerActionInProgress = false;
                viewerComponent.cd.detectChanges();

                if (seriesList != null) {
                    seriesList.forEach(item => {
                        // update general viewer data
                        item.seriesId = item.id;
                        item.seriesInstanceUID = item.seriesUID;
                        item.timepoint = timepoint
                        viewerComponent.viewerData[item.seriesId] = item;
                        viewerComponent.openNewSer([seriesId], viewerComponent.viewerData);
                    });
                } else {
                    viewerComponent.handleErr(null, seriesId);
                    viewerComponent.lastAction = 'WindowLevelInteractiveMode';
                    viewerComponent.lastInfo = null;
                    viewerComponent.onClose.emit({
                        serID: null, shouldCloseViewer: true
                    });
                }
            });
        });
      }
      else {
        viewerComponent.openNewSer([seriesId], viewerComponent.viewerData);
      }
    }

    isInstanceAscending(cell: lt.Controls.Medical.Cell){
      let isAscending = false
      const cellFrames: lt.Controls.Medical.Frame[] = cell.get_frames().toArray();
      if (cellFrames.length > 1){
          isAscending = cellFrames[0].instanceNumber < cellFrames[1].instanceNumber;
      }
      return isAscending;
    }

    get_colormap_from_mapname(map_name){
        let colorMap;
        switch(map_name) {
            case 'Initial Rate of Washout': // IRW
                colorMap = this.getYellowRedColorMap();
                break;
            case 'Gadolinium': // GD
                colorMap = this.getRGBColorMap();
                break;
            case 'Initial Rate of Enhancement': // IRE
                colorMap = this.getYellowRedColorMap();
                break;
            case 'Time of Onset': // Tonset
                colorMap = this.getJetColorMap();
                break;
            case 'Time to Washout': // Twashout
                colorMap = this.getJetColorMap();
                break;
            case 'Maximum Enhancement': // ME
                colorMap = this.getYellowRedColorMap();
                break;
            case 'Time To Peak': // TTP
                colorMap = this.getJetColorMap();
                break;
            case 'Area Under the Curve': // AUC
                colorMap = this.getBlueVioletColorMap();
                break;
            case 'rCBV Reg': // rCBV
                colorMap = this.getrCBVColorMap();
                break;
            case 'Tofts ve Reg': // Tofts VE
                colorMap = this.getToftsVEColorMap();
                break;
            case 'Tofts vp Reg': // Tofts VP
                colorMap = this.getToftsVPColorMap();
                break;
            case 'Tofts Ktrans Reg': // Tofts Ktrans
                colorMap = this.getToftsKtransColorMap();
                break;
            case 'FTB Reg': // FTB
                colorMap = this.getFTBColorMap();
                break;
            case 'K2 Reg': // K2
                colorMap = this.getK2ColorMap();
                break;
            case 'ADC Reg': // ADC
                colorMap = this.getADCColorMap();
                break;
            default:
                colorMap = this.getCustomGradient('#000000', '#FFFFFF');
        }
        return colorMap;
    }

  get_image_data(img){
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    context.drawImage(img, 0, 0);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    return imageData;
  }

  add_gradient_overlay_canvas(cell: lt.Controls.Medical.Cell, gd) {
    // console.log("add_gradient_overlay_canvas...");
    const cell_div = cell.get_div();
    const rangeData = gd.rangeData;
    const base64PNG = gd.base64PNG;

    const pm_subMenuItems = this.toolbarItems[cell.divID].pm_subMenuItems;
    const pm_subMenuItem = pm_subMenuItems.find(x => x.info.mapName === gd.mapName);

    const mapIdx = pm_subMenuItem.info.mapIdx;
    const mapSeriesUID = pm_subMenuItem.info.mapSeriesUID;
    const mapCode = 'DCE-'+String(mapIdx);

    const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
    const numbOfSlices = seriesInfo.numbOfSlices;
    const currentOffset = cell.get_currentOffset();
    const sliceOffet = this.utils.getRemainder(currentOffset, numbOfSlices);

    // load and store PNG images for the selected map
    const minVal = rangeData.min;
    const maxVal = rangeData.max;
    let canvasElt;

    const image = new Image();
    image.onload = () => {
      // build canvas for PM with colormap applied
      canvasElt = this.build_gradient_canvas(cell, image, gd.mapName, minVal, maxVal);

      // remove current canvas if it exists
      const cur_canvasElt = document.getElementById(cell.divID+'_gd-overlay') as HTMLCanvasElement;
      if(cur_canvasElt != null){
        cur_canvasElt.style.display = 'none';
        cell_div.removeChild(cur_canvasElt);
      }
      canvasElt.style.display = 'block';
      cell_div.appendChild(canvasElt);
    };
    image.src = 'data:image/png;base64,'+base64PNG;

    this._parametricMaps[cell.divID][mapCode][mapSeriesUID]['gradientDOM'] = image;
  }

  add_maps_overlay_canvas(cell: lt.Controls.Medical.Cell, sopInstanceUIDs, map_code, info) {
    // console.log('add_maps_overlay_canvas...');
    const cell_div = cell.get_div();
    const cellFrame: lt.Controls.Medical.Frame = cell.get_frames().get_item(0);
    const im_h = cellFrame.get_height();
    const im_w = cellFrame.get_width();

    const mapName = info.mapName;
    const mapSeriesUID = info.mapSeriesUID;

    const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
    const numbOfSlices = seriesInfo.numbOfSlices;
    const currentOffset = cell.get_currentOffset();
    const sliceOffet = this.utils.getRemainder(currentOffset, numbOfSlices);

    // load and store PNG images for the selected map
    for(let i=0; i<sopInstanceUIDs.length; i++){
      const sopiud = sopInstanceUIDs[i];
      const image = new Image();
      image.crossOrigin = 'Anonymous';
      let canvasElt;

      image.onload = () => {
        // build canvas for PM with colormap applied
        canvasElt = this.build_mapped_canvas(cell, image, mapName);

        if(i === sliceOffet){
          // remove current canvas if it exists
          const cur_canvasElt = document.getElementById(cell.divID+'_pm-overlay') as HTMLCanvasElement;
          if(cur_canvasElt != null){
              cur_canvasElt.style.display = 'none';
              cell_div.removeChild(cur_canvasElt);
          }
          canvasElt.style.display = 'block';
          const opacitySlicer = this.create_opacity_slicer_element(cell) as HTMLDivElement;
          const opacitySlicerInput = opacitySlicer.lastChild as HTMLInputElement;
          canvasElt.style.opcity = parseFloat(opacitySlicerInput.value);
          cell_div.appendChild(canvasElt);
        }
      };

      if (this.patientStudyID != 81274) {
        let param = "auth=" + encodeURIComponent(this.authenticationService.authenticationCode) + '&instance=' + sopiud;
        param += '&frame=0&mime=image/png&bp=0&qf=0&cx=' + String(im_w) +'&cy=' +String(im_h)+ '&data=null';
        param += '&jwt='  + this.authenticationService.jwt;
        image.src = this._retrieveLocalUrl + '/GetImageTile?' + param;

      } else {
        let mapNameX = "";
        switch (info.mapName) {
          case 'Initial Rate of Washout':
              mapNameX = "IRW";
            break;
          case 'Maximum Enhancement':
              mapNameX = "ME";
            break;
          case 'Time To Peak':
              mapNameX = "TTP";
            break;
          case 'Initial Rate of Enhancement':
              mapNameX = "IRE";
            break;
          case 'Time to Washout':
              mapNameX = "Twashout";
            break;
          case 'Gadolinium':
              mapNameX = "GD";
            break;
          case 'Area Under the Curve':
              mapNameX = "AUC";
            break;
          case 'Time of Onset':
              mapNameX = "Tonset";
            break;

          default:
              mapNameX = info.mapName;
            break;
        }
        let param = '&seriesID=' + seriesInfo.seriesId + '&mapName=' + mapNameX + '&sliceNumber=' + i + '&userData=null';
        image.src = this._retrieveLocalUrl + '/GetImageTileDyn5?' + param;
      }

      this._parametricMaps[cell.divID][map_code][mapSeriesUID]['imageDOM'].push(image);
    }

    // apply gradient for PM
    this.applyGradientPM(cell, info);
  }

  applyParametricMap(info){
    // console.log("applyParametricMap...");
    const locStorage = JSON.parse(localStorage.getItem('project'));
    this.patientStudyID = locStorage['id'];
    const cell: lt.Controls.Medical.Cell = this.seriesManagerService.getActiveCell();
    const cell_div = cell.get_div();
    const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);

    const mapIdx = info.mapIdx;
    const mapName = info.mapName;
    const mapSeriesUID = info.mapSeriesUID;

    const isMC = this._parametricMaps[cell.divID]['isMC'];
    const map_code = 'DCE-'+String(mapIdx);

    // remove existing gradient as soon as user selects new PM
    const cur_canvasElt = document.getElementById(cell.divID+'_gd-overlay') as HTMLCanvasElement;
    if(cur_canvasElt != null){
      cur_canvasElt.style.display = 'none';
      cell_div.removeChild(cur_canvasElt);
    }

    // add opacity slicer for PM
    const cur_opaSlicerElt = document.getElementById(cell.divID+'_opacity-slicer') as HTMLCanvasElement;
    if(cur_opaSlicerElt != null&&(<any>cell).pm_opacity){
      const cur_canvasElt = document.getElementById(cell.divID+'_pm-overlay')  as HTMLCanvasElement;
      cur_canvasElt.style.opacity = (<any>cell).pm_opacity.toString();
    }
    else{
      (<any>cell).pm_opacity = 0.8;

    }
    if(cur_opaSlicerElt == null) {
      const opacitySlicer = this.create_opacity_slicer_element(cell) as HTMLDivElement;
      const opacitySlicerInput = opacitySlicer.lastChild as HTMLInputElement;
      opacitySlicerInput.value = (<any>cell).pm_opacity.toString();

      opacitySlicerInput.onchange = () => {
        const cur_value = parseFloat(opacitySlicerInput.value);
        // update opacity value
        const cur_canvasElt = document.getElementById(cell.divID+'_pm-overlay')  as HTMLCanvasElement;
        cur_canvasElt.style.opacity = cur_value.toString();
        (<any>cell).pm_opacity = cur_value;
      };
      cell_div.appendChild(opacitySlicer);
    }
    else {
      if((<any>cell).pm_opacity) {
        const cur_canvasElt = document.getElementById(cell.divID+'_pm-overlay')  as HTMLCanvasElement;
        cur_canvasElt.style.opacity = (<any>cell).pm_opacity.toString();
      }
    }

    if (isMC) {
      const overlay = this.getOverlay(cell, 'mc-content');
      overlay.set_text(mapName);
      this.setOverlay(cell, 'mc-content', overlay);
    } else {
      const overlay = this.getOverlay(cell, 'pm-content');
      overlay.set_text(mapName);
      this.setOverlay(cell, 'pm-content', overlay);
    }

    if(this._parametricMaps[cell.divID]
      && this._parametricMaps[cell.divID][map_code]
      && this._parametricMaps[cell.divID][map_code][mapSeriesUID])
    {
      if (this._parametricMaps[cell.divID][map_code][mapSeriesUID] == "null")
      {
        const isAscending = this.isInstanceAscending(cell);
        const orderType = isAscending ? 'ASC' : 'DESC';

        const pmInfo_new = {
          'mapName': mapName,
          'sopInstanceUIDs': {},
          'imageDOM': [],
          'gradientDOM': []
        };
        const pmInfo = this._parametricMaps[cell.divID].pmInfos.find(pm => pm.seriesUID === mapSeriesUID);

        if(pmInfo.sopInstanceUIDs) {
          pmInfo_new.sopInstanceUIDs = pmInfo.sopInstanceUIDs;
          this._parametricMaps[cell.divID][map_code][mapSeriesUID] = pmInfo_new;
          this.add_maps_overlay_canvas(cell, pmInfo_new.sopInstanceUIDs, map_code, info);
        }
        else {
          this.objectRetrieveService.getInstanceStacks(
            mapSeriesUID, 'InstanceNumber', orderType).subscribe((result: any) =>
          {
            let sopInstanceUIDs: any;
            let mapSeriesID;
            if (result === undefined || result.length === 0) {
              this._parametricMaps[cell.divID]['pmInfos'].forEach(mapItem => {
                if (mapItem.seriesUID === mapSeriesUID) {
                  mapSeriesID = mapItem.childSeriesId;
                }
              });
              // upload the data again and try to load it
              return this.objectRetrieveService.customUpload(seriesInfo['bucket'], mapSeriesID, mapSeriesUID, info.audit).subscribe((resultT: any) => {
                if (resultT ===  'DONE') {
                  this.objectRetrieveService.getInstanceStacks(
                    mapSeriesUID, 'InstanceNumber', orderType).subscribe((resultX: any) =>
                  {
                    sopInstanceUIDs = resultX;
                    pmInfo_new.sopInstanceUIDs = resultX;
                    this._parametricMaps[cell.divID][map_code][mapSeriesUID] = pmInfo_new;
                    this.add_maps_overlay_canvas(cell, sopInstanceUIDs, map_code, info);
                  });
                }
              });
            } else {
              sopInstanceUIDs = result;
              pmInfo_new.sopInstanceUIDs = result;
              this._parametricMaps[cell.divID][map_code][mapSeriesUID] = pmInfo_new;
              this.add_maps_overlay_canvas(cell, sopInstanceUIDs, map_code, info);
            }
          });
        }
      }
      else {
        // load stored PNGs
        const numbOfSlices = seriesInfo.numbOfSlices;
        const phantomSliceNumb = this.getRemainder(cell.currentOffset, numbOfSlices);
        const cur_canvasElt = document.getElementById(cell.divID+'_pm-overlay') as HTMLCanvasElement;
        const imageMap = this._parametricMaps[cell.divID][map_code][mapSeriesUID]['imageDOM'][phantomSliceNumb];
        if(cur_canvasElt != null){
          cur_canvasElt.style.display = 'none';
          cell_div.removeChild(cur_canvasElt);
        }

        // build canvas for PM with colormap applied
        const new_canvasElt_map = this.build_mapped_canvas(cell, imageMap, mapName);
        // add new canvas to the cell
        new_canvasElt_map.style.display = 'block';
        new_canvasElt_map.style.opacity = (<any>cell).pm_opacity.toString();

        cell_div.appendChild(new_canvasElt_map);

        const imageGradient = this._parametricMaps[cell.divID][map_code][mapSeriesUID]['gradientDOM'];
        const gd = (<any>cell).gradientData && (<any>cell).gradientData[mapName] ? (<any>cell).gradientData[mapName] : null;

        const minVal = gd.rangeData.min;
        const maxVal = gd.rangeData.max;

        // build canvas for gradient with colormap applied
        const new_canvasElt_gd = this.build_gradient_canvas(cell, imageGradient, mapName, minVal, maxVal);
        // add new canvas to the cell
        new_canvasElt_gd.style.display = 'block';
        cell_div.appendChild(new_canvasElt_gd);
      }
    }
    else {
      console.log("Can't find PM...");
      console.log("map_code:", map_code);
      console.log("mapSeriesUID:", mapSeriesUID);
      console.log("rootSeriesId:", info.rootSeriesId);
      console.log('sel_seriesId:', (<any>cell).seriesId);
      // console.log('this._parametricMaps[cell.divID]:', this._parametricMaps[cell.divID]);
    }

    if (mapIdx == 'NONE'){
      // Disable parametric map overlay
      const cur_canvasElt = document.getElementById(cell.divID+'_pm-overlay')  as HTMLCanvasElement;
      if (cur_canvasElt != null){
        cur_canvasElt.style.display = 'none';
        cell_div.removeChild(cur_canvasElt);
      }
      else console.log('No PM canvas found...');

      // Remove Opacity Slicer
      const cur_opaSlicerElt = document.getElementById(cell.divID+'_opacity-slicer').parentNode as HTMLCanvasElement;
      if(cur_opaSlicerElt != null){
        cur_opaSlicerElt.style.display = 'none';
        cell_div.removeChild(cur_opaSlicerElt);
      }
    }

    this._parametricMaps[cell.divID]['mapNameSelected'] = mapName;
    this._parametricMaps[cell.divID]['mapCodeSelected'] = map_code;
    this._parametricMaps[cell.divID]['mapSeriesUIDSelected'] = mapSeriesUID;
    // console.log("this._parametricMaps: ", this._parametricMaps);
  }

  applyGradientPM(cell: lt.Controls.Medical.Cell, info) {
    // console.log('applyGradientPM...');
    const queryGradients = [];
    const cellsByLabel = {};
    const selMapName = info.mapName;

    const viewer = cell.get_viewer();
    const cells = viewer.layout.get_items().toArray();
    const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);

    // group cells by label
    cells.forEach(c => {
      const _cell: lt.Controls.Medical.Cell = c;
      const _seriesInfo = this.seriesManagerService.getSeriesInfo(_cell);
      if (seriesInfo.label === _seriesInfo.label) {
        if (!cellsByLabel[_seriesInfo.label]) {
          cellsByLabel[_seriesInfo.label] = [_cell];
        }
        else {
          cellsByLabel[_seriesInfo.label].push(_cell);
        }
      }
    });

    // prepare query gradient
    const prepareBatchQuery = async (seriesLabel) => {
      const _cells = cellsByLabel[seriesLabel];

      const queryGradient = new QueryGradient();
      queryGradient.seriesLabel = seriesLabel;
      queryGradient.seriesData = [];
      queryGradient.rootURL = ROOT_URL;
      queryGradient.gradientWidth = 100;
      queryGradient.gradientHeight = 400;
      if (this.patientStudyID != 81274) {
        queryGradient.isChanged = true;
      } else {
        queryGradient.isChanged = false;
      }
      queryGradient.gradientMethod = 'maximum';

      const prepareQueryGradient = async (_cell, _queryGradient) => {
        const _seriesInfo = this.seriesManagerService.getSeriesInfo(_cell);
        const visitName = _seriesInfo.visits[(<any>_cell).seriesId];
        const pm_subMenuItems = this.toolbarItems[_cell.divID].pm_subMenuItems;
        const pm_subMenuItem = pm_subMenuItems.find(x => x.info.mapName === selMapName);

        const mapIdx = pm_subMenuItem.info.mapIdx;
        const mapName = pm_subMenuItem.info.mapName;
        const mapSeriesUID = pm_subMenuItem.info.mapSeriesUID;
        const mapCode = 'DCE-'+String(mapIdx);

        const _seriesData = new SeriesData();
        _seriesData.divId = _cell.divID;
        _seriesData.seriesId = (<any>_cell).seriesId;
        _seriesData.visitName = visitName;
        _seriesData.mapInfo = new MapInfo();
        _seriesData.mapInfo.code = mapCode;
        _seriesData.mapInfo.name = mapName;
        _seriesData.mapInfo.seriesInstanceUID = mapSeriesUID;

        const parametricMaps = this.getParametricMaps(_cell);
        const pmInfo: any = parametricMaps.pmInfos.find(x => x.mapName == mapName);

        if (pmInfo.sopInstanceUIDs) {
          _seriesData.mapInfo.sopInstanceUIDs = pmInfo.sopInstanceUIDs;
          _queryGradient.seriesData.push(_seriesData);
          return new Promise<any>(resolve => {
            resolve('');
          });
        }
        else if (parametricMaps[mapCode] && parametricMaps[mapCode][mapSeriesUID]
        && parametricMaps[mapCode][mapSeriesUID].sopInstanceUIDs) {
          _seriesData.mapInfo.sopInstanceUIDs = parametricMaps[mapCode][mapSeriesUID].sopInstanceUIDs;
          _queryGradient.seriesData.push(_seriesData);
          return new Promise<any>(resolve => {
            resolve('');
          });
        }
        else {
          const isAscending = this.utils.isInstanceAscending(cell);
          const orderType = isAscending ? 'ASC' : 'DESC';

          return this.objectRetrieveService.getInstanceStacks(
            mapSeriesUID, 'InstanceNumber', orderType).toPromise().then((result: any) =>
          {
            pmInfo.sopInstanceUIDs = result;
            _seriesData.mapInfo.sopInstanceUIDs = result;
            _queryGradient.seriesData.push(_seriesData);
            // update PM to avoid duplicate actions
            this.setParametricMaps(_cell, parametricMaps);
          });
        }
      };

      return Promise.all(_cells.map(
        (_cell) => prepareQueryGradient(_cell, queryGradient))).then(() => {
        queryGradients.push(queryGradient);
      });
    };

    if (!(<any>cell).gradientData || !(<any>cell).gradientData[selMapName]){
      Promise.all(Object.keys(cellsByLabel).map(
        (seriesLabel) => prepareBatchQuery(seriesLabel))).then(() => {
        // get gradient data
        this.queryArchiveService.findGradientData(queryGradients).toPromise().then(gradients => {
          // console.log('gradients:', gradients);
          // display gradients on viewer cells
          gradients.forEach(gradient => {
            const gds: any[] = gradient.data ? gradient.data : [];

            gds.forEach(gd => {
              // get the cell related
              const _cell = cells.find(x => parseInt(x.seriesId) === parseInt(gd.seriesId));
              if (_cell) {
                if (!_cell.gradientData){
                  _cell.gradientData = {[selMapName]: gd};
                }
                else {
                  _cell.gradientData[selMapName] = gd;
                }
              }
            });
          });

          if ((<any>cell).gradientData && (<any>cell).gradientData[selMapName]){
            this.add_gradient_overlay_canvas(cell, (<any>cell).gradientData[selMapName]);
          }
        });
      });
    }
    else {
      // console.log('use gradient data cached');
      if ((<any>cell).gradientData && (<any>cell).gradientData[selMapName]){
        this.add_gradient_overlay_canvas(cell, (<any>cell).gradientData[selMapName]);
      }
    }
  }

  getParametricMaps(cell){
    return this._parametricMaps[cell.divID];
  }

  setParametricMaps(cell, pm){
    this._parametricMaps[cell.divID] = pm;
  }

  getROINormalizedPixelValuesArray(cell, numbOfSlices, numbOfFrames, roi) {
    const frames = cell.get_frames();
    const curSlice = cell.get_currentOffset();
    const phantomSlice = this.getRemainder(curSlice, numbOfSlices);
    const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
    const viewerComponent = seriesInfo.viewerComponent;
    const avgRoi = [];
    let avgRoiRange = 0.0;
    let frameIntensities = [];

    // ensure pixel data is ready for active and other frames
    viewerComponent.isIntensityChartActionInProgress = true;

    return this.seriesManagerService.loadPixelData(cell).then(() => {
      viewerComponent.isIntensityChartActionInProgress = false;
      viewerComponent.cd.detectChanges();
      const child: ltAnnotationsEngine.AnnObject = this.utils.getSelectedChild(cell, roi);
      if (child) {
        const parent = cell.get_frames().get_item(roi.slice - 1).get_container();
        const roiZone: lt.LeadRectD = child.getBoundingRectangle();
        const parentBounds: lt.LeadRectD = parent.get_bounds();
        try {
          for (let i = 0; i < numbOfFrames; i++) {
            const cellFrame: any = frames.get_item(i * numbOfSlices + phantomSlice);
            avgRoi[i] = this.getFrameAverageRoi(roiZone, parentBounds, cellFrame);
          }
          if (!seriesInfo.baselineRange || seriesInfo.baselineRange.length === 0) {
            seriesInfo.baselineRange = [1, 2];
          }
          seriesInfo.baselineRange.forEach(frameNum => {
            const cellFrame: any = frames.get_item(frameNum * numbOfSlices + phantomSlice);
            avgRoiRange += this.getFrameAverageRoi(roiZone, parentBounds, cellFrame);
          });
          avgRoiRange /= seriesInfo.baselineRange.length;
          frameIntensities = avgRoi.map(function (x) {
            return x / avgRoiRange;
          });
        } catch (err) {
          frameIntensities = [];
        }
      }
      return frameIntensities;
    });
    }

  getVOINormalizedPixelValuesArray(cell, numbOfSlices, numbOfFrames, voi) {
    const frames = cell.get_frames();
    const curSlice = cell.get_currentOffset();
    const phantomSlice = this.getRemainder(curSlice, numbOfSlices);
    const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
    const viewerComponent = seriesInfo.viewerComponent;
    let avgFrameVoi = [];
    let avgVoiRange = 0.0;
    let frameIntensities = [];

    // ensure pixel data is ready for active and other frames
    viewerComponent.isIntensityChartActionInProgress = true;

    return this.seriesManagerService.loadPixelData(cell).then(() => {
      viewerComponent.isIntensityChartActionInProgress = false;
      viewerComponent.cd.detectChanges();
      const rois = voi.srcChildren;
      try {
        for (let i = 0; i < numbOfFrames; i++) {
          rois.forEach(roi => {
            const parent = roi.get_parent();
            const roiZone: lt.LeadRectD = roi.getBoundingRectangle();
            const parentBounds: lt.LeadRectD = parent.get_bounds();
            const cellFrame: any = frames.get_item(i * numbOfSlices + phantomSlice);
            if (avgFrameVoi[i]) {
              avgFrameVoi[i] += this.getFrameAverageRoi(roiZone, parentBounds, cellFrame);
            } else {
              avgFrameVoi[i] = this.getFrameAverageRoi(roiZone, parentBounds, cellFrame);
            }
          });
        }
        avgFrameVoi = avgFrameVoi.map(v => v / rois.length);
        if (!seriesInfo.baselineRange || seriesInfo.baselineRange.length === 0) {
          seriesInfo.baselineRange = [1, 2];
        }
        seriesInfo.baselineRange.forEach(frameNum => {
          avgVoiRange += avgFrameVoi[frameNum];
        });
        avgVoiRange /= seriesInfo.baselineRange.length;
        frameIntensities = avgFrameVoi.map(function (x) {
          return x / avgVoiRange;
        });
      } catch (err) {
        frameIntensities = null;
      }
      return frameIntensities;
    });
  }

  private getFrameAverageRoi(roiZone: lt.LeadRectD, parentBounds: lt.LeadRectD, cellFrame: any) {
    let count = 0;
    let intensity = 0.0;
    const rel_w = cellFrame.get_width() / parentBounds.get_right();
    const rel_h = cellFrame.get_height() / parentBounds.get_bottom();
    for (let j = Math.round(roiZone.get_left() * rel_w); j <= Math.round(roiZone.get_right() * rel_w); j++) {
      for (let k = Math.round(roiZone.get_top() * rel_h); k <= Math.round(roiZone.get_bottom() * rel_h); k++) {
        intensity += cellFrame.pixelData[j][k];
        count++;
      }
    }
    return intensity / count;
  }

  getNormalizedPixelValuesArray(cell, rel_x, rel_y, numbOfSlices, numbOfFrames) {
    // console.log('getNormalizedPixelValuesArray...');
    let frames = cell.get_frames();
    const curSlice = cell.get_currentOffset();
    const phantomSlice = this.getRemainder(curSlice, numbOfSlices);
    const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
    const viewerComponent = seriesInfo.viewerComponent;
    let frameIntensities = [];
    const precoIntensities = [];

    // ensure pixel data is ready for active and other frames
    viewerComponent.isIntensityChartActionInProgress = true;

    return this.seriesManagerService.loadPixelData(cell).then(() => {
      viewerComponent.isIntensityChartActionInProgress = false;
      viewerComponent.cd.detectChanges();
      try {
        const num_precontrast = numbOfFrames > 1 ? 2 : 1;
        for (let i = 0; i < num_precontrast; i++) {
          const cellFrame: any = frames.get_item(i * numbOfSlices + phantomSlice);
          precoIntensities[i] = cellFrame.pixelData[rel_y][rel_x];
        }
        for (let i = 0; i < numbOfFrames; i++) {
          const cellFrame: any = frames.get_item(i * numbOfSlices + phantomSlice);
          const intensityI = cellFrame.pixelData[rel_y][rel_x];
          frameIntensities[i] = intensityI;
        }
        const avgInt = ((precoIntensities.reduce((a, b) => a + b, 0)) / precoIntensities.length) || 0;
        frameIntensities = frameIntensities.map(function(x) {return x / avgInt});
      } catch (err) {
        frameIntensities = null;
      }
      return frameIntensities;
    });
  }

  getAcquisitionTimes(cell) {
    const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
    let aqTimes = seriesInfo.aqTimes;
    let acquisitionTimes = [];

    if (!aqTimes) {
      aqTimes = seriesInfo.triggerTimes || [];
      aqTimes = aqTimes.map(t => t * 1000);
      if (!aqTimes) {
        aqTimes = {};
        for (let i = 0; i < seriesInfo.numbOfFrames; i++) {
          aqTimes[i] = i * 10;
        }
      }
    }
    for (let i = 0; i < Object.keys(aqTimes).length; i++) {
      acquisitionTimes[i] = aqTimes[i + 1];
    }

      let minTime = Math.min.apply(null, acquisitionTimes);
      acquisitionTimes = acquisitionTimes.map(function(x) {return x - minTime;});
      // console.log('acquisitionTimes:', acquisitionTimes);
      return acquisitionTimes;
  }

  actionProbeTool(cell) {
    // console.log('actionProbeTool...');
    const cell_div = cell.get_div();
    const cell_H = cell_div.clientHeight;
    const cell_W = cell_div.clientWidth;

    const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
    const numbOfSlices = seriesInfo.numbOfSlices;
    const numbOfFrames = seriesInfo.numbOfFrames;

    cell_div.addEventListener('mousemove', (event) => {
        const cur_x = event.offsetX;
        const cur_y = event.offsetY;
        const cellFrame: lt.Controls.Medical.Frame = cell.get_frames().get_item(cell.get_currentOffset());
        const im_h = cellFrame.get_height();
        const im_w = cellFrame.get_width();

        let bg_h, bg_w;
        if (im_h == im_w){
            bg_h = (cell_H < cell_W)? cell_H: cell_W;
            bg_w = bg_h;
        } else {
            bg_h = cell_H;
            bg_w = cell_W;
        }

        const mg_x = (cell_W - bg_h)/2;
        const mg_y = (cell_H - bg_w)/2;
        const rel_x = Math.round(((cur_x-mg_x)*im_w)/bg_w);
        const rel_y = Math.round(((cur_y-mg_y)*im_h)/bg_h);

        let rgbValue = lt.Controls.Medical.ProbeToolInteractiveMode.getPixelValue(cellFrame, rel_x, rel_y);
        let intensity = (rgbValue == null) ? 0 : rgbValue;

        // console.log("intensity: ", intensity);

        // console.log("this.name2OverlayIds: ", this.name2OverlayIds);
        // console.log("cell.get_overlays(): ", cell.get_overlays());

        // let overlay = this.getOverlay(cell, "pixel-bounds");
        // overlay.set_text("Bounds (x,y): ("+String(rel_x) +","+String(rel_y)+")");
        // this.setOverlay(cell, "pixel-bounds", overlay);

        // overlay = this.getOverlay(cell, "pixel-intensity");
        // overlay.set_text("Intensity: "+String(intensity));
        // this.setOverlay(cell, "pixel-intensity", overlay);
    });
  }

  updatenormalisedIntensityData(data, key, value) {
    data[key] = value;
  }

  deletenormalisedIntensityData(data, key) {
    delete data[key];
  }

    drawNormalisedIntensityGraph(data) {
        // console.log('drawNormalisedIntensityGraph...');
        const dataReady = data.names.map(n => ({
            name: n.toUpperCase(),
            values: data.times.map(function(t, i) {
                return {time: t, value: data[n][i] };
            }),
          color: data.colors.find(c => c.name === n).value
        }));

        if (dataReady.length === 4) {
          console.log(dataReady[1].values === dataReady[3].values);
        }

        let y_max = 1.0;
        dataReady.forEach((val) => {
          val.values.forEach((val1) => {
            if (val1.value > y_max) {
              y_max = val1.value;
            }
          });
        });
        const totalWidth = 500;
        const totalHeight = 210;
        const margin = {top: 30,
            right: 80,
            bottom: 30,
            left: 51};
        // axes dimensions
        const width = totalWidth - margin.left - margin.right;
        const height = totalHeight - margin.top - margin.bottom;

        if (this._normalisedIntensityGraph.svg === undefined) {
            this._normalisedIntensityGraph.svg = d3.select('#normalised-intensity-graph')
            .append('svg')
                .attr('width', width + margin.left + margin.right)
                .attr('height', height + margin.top + margin.bottom)
            .append('g')
                .attr('transform',
                    'translate(' + margin.left + ',' + margin.top + ')');

            // x-axis label
            this._normalisedIntensityGraph.svg.append('text')
                .attr('x', width / 2)
                .attr('y', totalHeight - margin.top)
                .style('text-anchor', 'middle')
                .style('font-size', 12)
                .text('Time (ms)');

            // y-axis label
            this._normalisedIntensityGraph.svg.append('text')
                .attr('transform', 'rotate(-90)')
                .attr('y', -41)
                .attr('x', -height / 2)
                .attr('dy', '1em')
                .style('text-anchor', 'middle')
                .style('font-size', 12)
                .text('Norm. Intensity');
        }

      this._normalisedIntensityGraph.svg.selectAll('g').remove();

      // x axis
      this._normalisedIntensityGraph.x = d3.scaleLinear()
        .domain([0, Math.max.apply(null, data.times)])
        .range([ 0, width]);
      this._normalisedIntensityGraph.svg.append('g')
        .attr('transform', 'translate(0,' + height + ')')
        .call(d3.axisBottom(this._normalisedIntensityGraph.x)
          .ticks(10));

      // y axis
      this._normalisedIntensityGraph.y = d3.scaleLinear()
        .domain( [0, parseFloat(y_max.toFixed(2))])
        .range([ height, 0]);
      this._normalisedIntensityGraph.svg.append('g')
        .call(d3.axisLeft(this._normalisedIntensityGraph.y)
          .ticks(10));

        const x = this._normalisedIntensityGraph.x;
        const y = this._normalisedIntensityGraph.y;
        const svg = this._normalisedIntensityGraph.svg;

        svg.selectAll('path#lines').remove();
        svg.selectAll('text#legend').remove();

        // plot lines
      const line = d3.line()
        .defined(d => !d.values)
        .x(function(d: any) { return x(+d.time); })
        .y(function(d: any) { return y(+d.value); });
      const lines = svg.selectAll('lines')
        .data(dataReady).attr('class', 'line');

      lines.enter()
        .append('path')
        .attr('class', function(d) { return d.name; })
        .attr('id', 'lines')
        .attr('d', function(d) { return line(d.values); })
        .attr('stroke', function (d) {return d.color; } )
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round')
        .style('stroke-width', 3)
        .style('fill', 'none');

        // this.svg.selectAll("myDots").remove();

        // const dots = this.svg.selectAll("myDots")
        //     .data(dataReady);
        // dots.enter()
        //     .append('g')
        //         .style("fill", "orange")
        //         .attr("class", function(d){ return d.name })

        // const points = svg.selectAll("myPoints")
        //     .data(function(d){ return d.values })
        // points.exit()
        //     .transition()
        //     .duration(1)
        //     .remove();
        // points.enter()
        //     .append("circle")
        //         .attr("cx", function(d) { return x(d.time) } )
        //         .attr("cy", function(d) { return y(d.value) } )
        //         .attr("r", 3)

      // Add a legend at the end of each line
        svg
          .selectAll('legend')
          .data(dataReady)
          .enter()
          .append('g')
          .append('text')
          .datum(function(d) { return {name: d.name, value: d.values[d.values.length - 1], color: d.color}; }) // keep only the last value of each time series
          .attr('id', 'legend')
          .attr('transform', function(d) { return 'translate(' + x(d.value.time) + ',' + y(d.value.value) + ')'; }) // Put the text at the position of the last point
          .attr('x', 2) // shift the text a bit more right
          .text(function(d) { return d.name; })
          .style('fill', function (d) {return d.color; })
          .style('font-size', 12);
    }

    playVideo(seriesInfo, cell, frame_tracker_id, timeline_id, speed) {
        const timerID = setInterval(() => {
            const numbOfFrames = seriesInfo.numbOfFrames;
            const curFrameNumb = seriesInfo.curFrameNumb ? seriesInfo.curFrameNumb : 1;
            const newFrameNumb = (curFrameNumb < numbOfFrames)
                ? curFrameNumb + 1
                : 1;

            this.manageFrameChange(seriesInfo, cell, frame_tracker_id, timeline_id, newFrameNumb);
        }, speed);

        return timerID;
    }

    createTimeLine(cell, seriesInfo): string {
        const aqTimes = seriesInfo.aqTimes;
        const cell_div = cell.get_div();
        const timeline_div = document.createElement("div") as HTMLDivElement;
        const timeline_id = cell.divID + "_timeline";
        const dash = (frameNumber) =>
            `<div data-frameNumber=${frameNumber} data-timeDifference='${aqTimes[frameNumber]}' class='${frameNumber == 1 ? 'timeline__dash-active' : ''} timeline__dash'></div>`;
        const frameIndicator = (frameNumber) => `<div data-frameNumber=${frameNumber} class='timeline__frame_number'></div>`;
        timeline_div.setAttribute("id", timeline_id);
        timeline_div.setAttribute("class", 'timeline');

        for (let i = 1; i <= seriesInfo.numbOfFrames; ++i) {
            timeline_div.insertAdjacentHTML('beforeend', dash(i));
        }
        cell_div.appendChild(timeline_div);

        return timeline_id;
    }

    createFrameBtn(cell, side, text) {
        const cell_div = cell.get_div();
        const btn_div = document.createElement("div") as HTMLDivElement;
        const frame_id = `${cell.divID}_change_frame-${side}`;

        btn_div.innerHTML = text;
        btn_div.setAttribute("id", frame_id);
        btn_div.setAttribute("class", `change_frame change_frame-${side}`);

        cell_div.appendChild(btn_div);

        return frame_id;
    }

    createFrameBtns(cell) {
        const cell_div = cell.get_div();
        let dceGroupDiv = document.createElement('div');
        let dceGroupId = cell.divID+"_dceGroup";

        let frameTrackerStr = `<div _ngcontent-slk-c358='' id='`+dceGroupId+`' class='overlay-hidden dce-group'><button _ngcontent-slk-c358='' mat-mini-fab='' mini='' icon='play-arrow' class='mat-focus-indicator menuportlet change_frame change_frame-left_video mat-mini-fab mat-button-base mat-accent'><span class='mat-button-wrapper'><mat-icon _ngcontent-slk-c358='' role='img' class='mat-icon notranslate material-icons mat-icon-no-color' aria-hidden='true'>play_arrow</mat-icon></span><div matripple='' class='mat-ripple mat-button-ripple mat-button-ripple-round' ng-reflect-disabled='false' ng-reflect-centered='false' ng-reflect-trigger='[object HTMLButtonElement]'></div><div class='mat-button-focus-overlay'></div></button><button _ngcontent-slk-c358='' mat-mini-fab='' mini='' icon='play-arrow' class='mat-focus-indicator menuportlet change_frame change_frame-right_video mat-mini-fab mat-button-base mat-accent'><span class='mat-button-wrapper'><mat-icon _ngcontent-slk-c358='' role='img' class='mat-icon notranslate material-icons mat-icon-no-color' aria-hidden='true'>pause</mat-icon></span><div matripple='' class='mat-ripple mat-button-ripple mat-button-ripple-round' ng-reflect-disabled='false' ng-reflect-centered='false' ng-reflect-trigger='[object HTMLButtonElement]'></div><div class='mat-button-focus-overlay'></div></button><button _ngcontent-slk-c358='' mat-mini-fab='' mini='' icon='navigate-next' class='mat-focus-indicator menuportlet change_frame change_frame-right mat-mini-fab mat-button-base mat-accent'><span class='mat-button-wrapper'><mat-icon _ngcontent-slk-c358='' role='img' class='mat-icon notranslate material-icons mat-icon-no-color' aria-hidden='true'>navigate_next</mat-icon></span><div matripple='' class='mat-ripple mat-button-ripple mat-button-ripple-round' ng-reflect-disabled='false' ng-reflect-centered='false' ng-reflect-trigger='[object HTMLButtonElement]'></div><div class='mat-button-focus-overlay'></div></button><button _ngcontent-slk-c358='' mat-mini-fab='' mini='' icon='navigate-before' class='mat-focus-indicator menuportlet change_frame change_frame-left mat-mini-fab mat-button-base mat-accent'><span class='mat-button-wrapper'><mat-icon _ngcontent-slk-c358='' role='img' class='mat-icon notranslate material-icons mat-icon-no-color' aria-hidden='true'>navigate_before</mat-icon></span><div matripple='' class='mat-ripple mat-button-ripple mat-button-ripple-round' ng-reflect-disabled='false' ng-reflect-centered='false' ng-reflect-trigger='[object HTMLButtonElement]'></div><div class='mat-button-focus-overlay'></div></button><button _ngcontent-slk-c358='' mat-mini-fab='' mini='' icon='speed' class='mat-focus-indicator menuportlet change_frame change_frame-speed_video mat-mini-fab mat-button-base mat-accent'><span class='mat-button-wrapper'><mat-icon _ngcontent-slk-c358='' role='img' class='mat-icon notranslate material-icons mat-icon-no-color' aria-hidden='true'>speed</mat-icon></span><div matripple='' class='mat-ripple mat-button-ripple mat-button-ripple-round' ng-reflect-disabled='false' ng-reflect-centered='false' ng-reflect-trigger='[object HTMLButtonElement]'></div><div class='mat-button-focus-overlay'></div></button></div>`;
        dceGroupDiv.innerHTML = frameTrackerStr.trim();
        const dceGroup = dceGroupDiv.firstChild as HTMLDivElement;
        cell_div.appendChild(dceGroup);

        const nextFrameBtn = dceGroup.querySelector('.change_frame-right')
        const prevFrameBtn = dceGroup.querySelector('.change_frame-left')
        const videoPlayBtn = dceGroup.querySelector('.change_frame-left_video')
        const videoStopBtn = dceGroup.querySelector('.change_frame-right_video')
        const videoSpeedBtn = dceGroup.querySelector('.change_frame-speed_video')
        return { nextFrameBtn, prevFrameBtn, videoPlayBtn, videoStopBtn, videoSpeedBtn };
    }

    createDCELayout(cell, seriesInfo) {
        const timeline_id = this.createTimeLine(cell, seriesInfo);
        const speedLabel_id = this.createSpeedLabel(cell);
        const {
            nextFrameBtn,
            prevFrameBtn,
            videoPlayBtn,
            videoStopBtn,
            videoSpeedBtn
        } = this.createFrameBtns(cell);

        return {
            timeline_id,
            nextFrameBtn,
            prevFrameBtn,
            videoPlayBtn,
            videoStopBtn,
            videoSpeedBtn,
            speedLabel_id
        };
    }

    manageFrameChange(seriesInfo, cell, frame_tracker_id, timeline_id, newFrameNumb) {
        this.setNewFrame(seriesInfo, cell, newFrameNumb);
        this.changeFrameInput(frame_tracker_id, newFrameNumb);
        this.changeTimeLine(timeline_id, newFrameNumb);
    }

    changeFrameInput(id, newFrameNumb) {
        const input = document.getElementById(id + "_current") as HTMLInputElement;
        input.value = String(newFrameNumb);
    }

    changeTimeLine(id, newFrameNumb) {
        const timeLine = document.getElementById(id);
        const newFrame = timeLine.querySelector(`[data-frameNumber="${newFrameNumb}"]`);
        const allItems: Element[] = Array.from(timeLine.children);

        allItems.map(item =>
                item.classList.remove('timeline__dash-active'));
        newFrame.classList.add('timeline__dash-active');
    }

    createSpeedLabel(cell) {
        const cell_div = cell.get_div();
        const speedLabel = document.createElement("div") as HTMLDivElement;
        const speedLabel_id = cell.divID + "_speedLabel";

        speedLabel.setAttribute("id", speedLabel_id);
        speedLabel.setAttribute("class", 'speedLabel change_frame');

        const content = () =>
            `<span id='${cell.divID}_speed'>1</span>x`

        speedLabel.insertAdjacentHTML('beforeend', content());

        cell_div.appendChild(speedLabel);

        return speedLabel_id;
    }

    getRemainder(divident, divisor) {
        return divident % divisor;
    }

    setNewFrame(seriesInfo, cell, newFrameNumb: number) {
        seriesInfo.curFrameNumb = newFrameNumb;
        this.cellOverlays[cell.divID].begTime = seriesInfo.aqTimes[newFrameNumb] ? seriesInfo.aqTimes[newFrameNumb] : null;

        // update begtime value
        const begtime_span = document.getElementById(cell.divID + "_frame-tracker_begtime") as HTMLInputElement;
        begtime_span.style.display = "inline";

        const begtimeval_span = document.getElementById(cell.divID + "_frame-tracker_begtime_val") as HTMLInputElement;
        if (this.cellOverlays[cell.divID].framesCount > 1 && this.cellOverlays[cell.divID].begTime === null) {
          this.cellOverlays[cell.divID].begTime = 0;
        }
        begtimeval_span.textContent = this.cellOverlays[cell.divID].begTime;

        const curSlice = cell.currentOffset;
        const phantomSlice = this.getRemainder(curSlice, seriesInfo.numbOfSlices);
        const newSliceNumb = (newFrameNumb - 1) * seriesInfo.numbOfSlices + phantomSlice;
        console.log('phantomSlice:', phantomSlice);
        console.log('newSliceNumb:', newSliceNumb);

        this.setNewSlice(cell, newSliceNumb);
    }

    setNewSlice(cell, newSliceNumb: number) {
        cell.currentOffset = newSliceNumb;
    }

    setCellOverlays(cell, metadata, isWaveForm) {
        const overlays: OverlayTags = this.getOverlayTags(cell, metadata);
        let currentAlignment;

        const addTagFunction = (index, value) => {
            const overlayTag: OverlayTag = value;

            if (currentAlignment === lt.Controls.Medical.OverlayAlignment.bottomRight) {
                index += 1;
            }

            if (overlayTag.tag != null && overlayTag.tag.length > 0) {
                let element;
                element = metadata[overlayTag.tag];

                let text = '';
                if (element && element.Value && element.Value.length > 0 && element.vr === 'PN') {
                    text = this.dicomHelperService.getPatientName(metadata, overlayTag.tag);
                }
                else {
                    text = element ? this.dicomHelperService.getTagValue(metadata, overlayTag.tag) : '';
                }

                if (element && element.Value && element.Value.length > 0 && element.vr === 'DA')
                    text = this.dicomHelperService.parseDicomDate(text, {month: true, day: false, year: true});

                if (element && element.Value && element.Value.length > 0 && element.vr === 'TM')
                    text = this.dicomHelperService.parseDicomTime(text);

                this.addTag(cell, overlayTag.title + text, currentAlignment, lt.Controls.Medical.OverlayTextType.userData, index, isWaveForm ? "rgba(0, 0, 0, 1)" : overlayTag.color);
            }
            else if (overlayTag.overlayType != null) {
                if (!isWaveForm) {
                    const overlayNames = ["image-spacing", "image-size", "pixel-intensity", "pixel-bounds", "pm-content", "mc-content", "TE/TR"];
                    if (overlayTag.name && overlayTag.name != null && overlayNames.includes(overlayTag.name)){
                        this.addTag(cell, overlayTag.title, currentAlignment,
                            overlayTag.overlayType, index, overlayTag.color, overlayTag.name);
                    } else this.addTag(cell, overlayTag.title, currentAlignment,
                            overlayTag.overlayType, index, overlayTag.color);
                }
            }
        };

        currentAlignment = lt.Controls.Medical.OverlayAlignment.topRight;
        $.each(overlays.topRight, $.proxy(addTagFunction, this));

        currentAlignment = lt.Controls.Medical.OverlayAlignment.topLeft;
        $.each(overlays.topLeft, $.proxy(addTagFunction, this));

        // console.log("cell.constructor.name: ", cell.constructor.name);
        // if (!(cell instanceof lt.Controls.Medical.Cell3D) && !(cell instanceof lt.Controls.Medical.STLCell))
        if(cell instanceof lt.Controls.Medical.Cell)
        {
            currentAlignment = lt.Controls.Medical.OverlayAlignment.bottomRight;
            $.each(overlays.bottomRight, $.proxy(addTagFunction, this));

            currentAlignment = lt.Controls.Medical.OverlayAlignment.bottomLeft;
            $.each(overlays.bottomLeft, $.proxy(addTagFunction, this));

            currentAlignment = lt.Controls.Medical.OverlayAlignment.centerLeft;
            $.each(overlays.centerLeft, $.proxy(addTagFunction, this));

            currentAlignment = lt.Controls.Medical.OverlayAlignment.centerTop;
            $.each(overlays.centerTop, $.proxy(addTagFunction, this));

            currentAlignment = lt.Controls.Medical.OverlayAlignment.centerRight;
            $.each(overlays.centerRight, $.proxy(addTagFunction, this));

            currentAlignment = lt.Controls.Medical.OverlayAlignment.centerBottom;
            $.each(overlays.centerBottom, $.proxy(addTagFunction, this));
        }

        // add custom overlays: slice & frame trackers
        if (cell instanceof lt.Controls.Medical.Cell) {
            // Slice & frame tracker overlays
            // refactor later!
            try {
                this.addCustomOverlays(cell);
            } catch (e) {
                console.log('reassigning the overlay. catch error', e);
                // console.log('reassigning the overlay. catch error:', e);
                setTimeout(() => {
                    this.addCustomOverlays(cell);
                }, 4000);
            }

            // Enable probeTool on mouse hover event & update overlays
            // this.actionProbeTool(cell);
        }
    }

    getOverlayTags(cell, metadata?): OverlayTags {
        let tags: OverlayTags;

        if (!this._defaultOverlayTags[cell.divID] || this._defaultOverlayTags[cell.divID] == null) {
            this._defaultOverlayTags[cell.divID] = new OverlayTags();
            this.buildDefaultOverlay(cell, metadata);
        }
        tags = this._defaultOverlayTags[cell.divID];

        return tags;
    }

    buildDefaultOverlay(cell, metadata?) {
        const seriesInfo = this.seriesManagerService.getSeriesInfo(cell);
        const { shouldShowDICOM, shouldHidePatient } = seriesInfo;

        // Overlays: top-left
        this.addDefaultTag(this._defaultOverlayTags[cell.divID].topLeft, "", undefined, undefined);

        // const patient_code = seriesInfo.visit.visitBlindName ? seriesInfo.visit.visitBlindName: 'Blinded';
        const patient_code = seriesInfo.patient.patientCode;
        this.addDefaultTag(
            this._defaultOverlayTags[cell.divID].topLeft, 'Patient: ' + patient_code,
            undefined, lt.Controls.Medical.OverlayTextType.userData, undefined, "patient-code");

        // this.addDefaultTag(
        //     this._defaultOverlayTags[cell.divID].topLeft, "Label: "+seriesInfo.label,
        //     undefined, lt.Controls.Medical.OverlayTextType.userData, undefined, "series-label");


        // Overlays: top-right
        if (!(cell instanceof lt.Controls.Medical.Cell3D)){
            this.addDefaultTag(
                this._defaultOverlayTags[cell.divID].topRight, "",
                undefined, lt.Controls.Medical.OverlayTextType.userData);
            this.addDefaultTag(
                this._defaultOverlayTags[cell.divID].topRight, "",
                undefined, lt.Controls.Medical.OverlayTextType.userData);
            this.addDefaultTag(
                this._defaultOverlayTags[cell.divID].topRight, "Motion Correction:",
                undefined, lt.Controls.Medical.OverlayTextType.userData);
            this.addDefaultTag(
                this._defaultOverlayTags[cell.divID].topRight, "None",
                undefined, lt.Controls.Medical.OverlayTextType.userData, undefined, "mc-content");
            this.addDefaultTag(
                this._defaultOverlayTags[cell.divID].topRight, "Parametric Map:",
                undefined, lt.Controls.Medical.OverlayTextType.userData);
            this.addDefaultTag(
                this._defaultOverlayTags[cell.divID].topRight, "None",
                undefined, lt.Controls.Medical.OverlayTextType.userData, undefined, "pm-content");
        }

        // Overlays: bottom-left
        try {
            if (seriesInfo.isDCE) {
                this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomLeft, "", undefined, undefined); // empty space for custom overlays
                this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomLeft, "", undefined, undefined); // empty space for custom overlays
            }
        } catch(e) {
        }

        this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomLeft, "", undefined, undefined); // empty space for custom overlays
        this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomLeft, "", DicomTag.SeriesDescription);

        let TE = this.dicomHelperService.getDicomTagValue(metadata, DicomTag.EchoTime);
        let TR = this.dicomHelperService.getDicomTagValue(metadata, DicomTag.RepetitionTime);
        TE = (TE == "undefined" || TE == null) ? "--": TE;
        TR = (TR == "undefined" || TR == null) ? "--": TR;
        this.addDefaultTag(
            this._defaultOverlayTags[cell.divID].bottomLeft, "TE/TR: "+" "+TE.toString()+" / "+TR.toString()+" ms",
            undefined, lt.Controls.Medical.OverlayTextType.userData, undefined, "TE/TR");

        this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomLeft, "Slice Thickness: ", DicomTag.SliceThickness);

        this.addDefaultTag(
            this._defaultOverlayTags[cell.divID].bottomLeft, "Spacing: ",
            undefined, lt.Controls.Medical.OverlayTextType.userData, undefined, "image-spacing");
        this.addDefaultTag(
            this._defaultOverlayTags[cell.divID].bottomLeft, "Image Size, px: ",
            undefined, lt.Controls.Medical.OverlayTextType.userData, undefined, "image-size");

        this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomLeft, "", undefined, lt.Controls.Medical.OverlayTextType.fieldOfView);


        // Overlays: bottom-right
        try {
            if (seriesInfo.isDCE) {
                this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomRight, "", undefined, undefined); // empty space for custom overlays
                this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomRight, "", undefined, undefined); // empty space for custom overlays
            }
        } catch(e) {
        }

        const selectedPage = seriesInfo['selectedPage'];
        const studyUserRoles =  seriesInfo['studyUserRoles'];
        const userRoleNames = studyUserRoles ? studyUserRoles.map(item => item.roleName) : [];
        // console.log("studyUserRoles: ", studyUserRoles);

        // let visitName = seriesInfo.visit.visitName ? seriesInfo.visit.visitName : 'Missing';
        // console.log("seriesInfo: ", seriesInfo);
        let visitName = seriesInfo.visits[(<any>cell).seriesId];

        // if (studyUserRoles.some(item => (item.roleType === 'READER') &&
        //    (selectedPage === 'Reading' || selectedPage === 'StudyFinder'))){
        //     visitName = 'Blinded';
        // }
        // if (studyUserRoles.some(item => (item.roleType === 'ADMIN' || item.roleType === 'TECH'))){
        //     visitName = seriesInfo.visit.visitName ? seriesInfo.visit.visitName : 'Missing';
        // }
        this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomRight, 'Visit Name: ' + visitName, visitName);

        if (shouldShowDICOM) {
            try {
              const date = this.dicomHelperService.getDicomTagValue(metadata, DicomTag.StudyDate);
              if (date === '') {
                this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomRight, "Scan Date: " + '', '');
              } else {
                const month = date.getMonth();
                const year = date.getFullYear();
                const monthObj = {
                    0: 'JAN', 1: 'FEB', 2: 'MAR', 3: 'APR', 4: 'MAY', 5: 'JUN',
                    6: 'JUL',7: 'AUG', 8: 'SEP', 9: 'OCT', 10: 'NOV', 11: 'DEC'
                };

                const string = '' + monthObj[month] + year;
                this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomRight, "Scan Date: " + string, string);
              }
            } catch (error) {
              console.log('Error... ', error);
            }
        }
        else {
            this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomRight, "Scan Date: Blinded", "Blinded");
        }
        this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomRight, "YOB: ", DicomTag.PatientBirthDate);
        this.addDefaultTag(this._defaultOverlayTags[cell.divID].bottomRight, "", undefined, lt.Controls.Medical.OverlayTextType.mprType);

        // Overlays: center
        this.addDefaultTag(this._defaultOverlayTags[cell.divID].centerLeft, "", undefined, lt.Controls.Medical.OverlayTextType.leftOrientation);
        this.addDefaultTag(this._defaultOverlayTags[cell.divID].centerTop, "", undefined, lt.Controls.Medical.OverlayTextType.topOrientation);
        this.addDefaultTag(this._defaultOverlayTags[cell.divID].centerRight, "", undefined, lt.Controls.Medical.OverlayTextType.rightOrientation);
        this.addDefaultTag(this._defaultOverlayTags[cell.divID].centerBottom, "", undefined, lt.Controls.Medical.OverlayTextType.bottomOrientation);
    }

    addDefaultTag(location: Array<OverlayTag>, title: string, dicomTag?: string, overlayType?: number, color?: string, name?: string) {
      try {
        let tag: OverlayTag;

        tag = new OverlayTag();
        tag.tag = dicomTag;
        tag.title = title;
        tag.overlayType = overlayType;
        tag.id = UUID.generate();
        tag.color = color == undefined ? "#FFFFFF" : color;
        tag.name = name;

        location.push(tag);
      } catch (error) {
        console.log('Error... ', error);
      }
    }

    initializeCellOverlay(cell): any {
        const framesCount = String(cell.get_frames().count);
        const sliceCount = this.seriesManagerService.getInstances(cell.seriesInstanceUID, cell.divID).length;
        // console.log("framesCount:", framesCount);
        // console.log("sliceCount:", sliceCount);

        const cellOverlay = {
            "isReady": true,
            "cellDivID": cell.divID,
            "closeBtnID": cell.divID+"_closeBtn",
            "dceGroupID": cell.divID+"_dceGroup",
            "cellOverlayID": cell.divID+"_overlays",
            "frameTrackerId": cell.divID + "_frame-tracker",
            "frameTrackerIdCurrent": cell.divID + "_frame-tracker_current",
            "framesCount": framesCount,
            "frameCurrent": 1,
            "sliceTrackerId": cell.divID + "_slice-tracker",
            "sliceTrackerIdCurrent": cell.divID + "_slice-tracker_current",
            "sliceCount": sliceCount,
            "sliceCurrent": 1,
            "begTime": null,
            "isDCE": false,
            "spinnerId": cell.divID+"_loader",
        };
        // console.log("cellOverlay:", cellOverlay);
        this.cellOverlays[cell.divID] = cellOverlay;
    }

}
